import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppStateService } from '../../services/app-state.service';
import { AuthService } from '../../services/auth.service';
import { WallpaperService } from '../../services/wallpaper.service';
import { JourneyContext, INITIAL_CONTEXT, SearchWallpaper, MasterItem, MastersResponse, buildPdpUrl } from '../../models/wallpaper.model';
import { DataLayerService } from '../../services/datalayer.service';
import { TriggerService } from '../../services/trigger.service';
import { PdpQuoteErrors, PdpQuoteInstallation, PdpQuoteSummary, quoteErrorsFrom, toQuoteSummary, withoutWall } from './quote-summary';

const NO_QUOTE_ERRORS: PdpQuoteErrors = { byWall: {}, general: null };
/** Shown when the server refuses a quote without a message of its own. */
const QUOTE_FAILED_MESSAGE = 'We could not price these walls. Please check the sizes and try again.';

@Component({
  selector: 'app-pdp',
  templateUrl: './pdp.component.html'
})
export class PdpComponent implements OnInit, OnDestroy {
  wallpaper: any = null;
  papers: any[] = [];
  selectedPaper: any = null;
  similarWallpapers: SearchWallpaper[] = [];

  /** True while the Similar rail is still resolving (it has three fallbacks). */
  similarLoading = false;

  /**
   * How many similar designs the rail shows. Was the literal 6 in three separate
   * `slice(0, 6)` calls and a fourth time in the template's placeholder count —
   * four copies of one layout decision, so changing the rail meant finding all of
   * them. The skeleton binds to this too, which is what keeps the placeholder grid
   * the same size as the real one.
   */
  readonly similarCount = 6;
  masterColors: MasterItem[] = [];
  resolvedColors: { name: string; hex: string }[] = [];
  wishlistIds = new Set<string>();
  context: JourneyContext = { ...INITIAL_CONTEXT };

  // Product & productType from init API (needed for cart)
  product: any = { id: 1 };
  productType: any = { id: 9, name: 'Wallpaper' };
  spaceLabels: { [key: string]: string } = {};

  isLoading = true;
  loadError = false;

  activeImageIndex = 0;
  selectedColorIndex = 0;
  quantity = 1;
  private currentSlug = '';
  isZoomed = false;
  isLightboxOpen = false;
  isCropModalOpen = false;
  croppedImageBase64 = '';
  cropperReady = false;
  activeTab = 'details';

  @ViewChild('thumbStrip') thumbStripEl!: ElementRef<HTMLDivElement>;
  @ViewChild('surfacesTab') surfacesTabEl?: ElementRef<HTMLDivElement>;

  scrollToSurfacesTab() {
    setTimeout(() => {
      const el = this.surfacesTabEl?.nativeElement;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Only scroll if element is offscreen or low; keep ~140px of context above the heading
      if (rect.top < 100 || rect.top > window.innerHeight - 100) {
        const targetY = window.scrollY + rect.top - 140;
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      }
    }, 0);
  }

  readonly surfaceImages: Record<string, string> = {
    'brush stroke':   'https://storage.googleapis.com/static-mahattaart-online/surface/Brush%20Stroke%20Wallpaper_V2.png',
    'canvas 220':     'https://storage.googleapis.com/static-mahattaart-online/surface/Canvas%20220%20Gsm%20Wallpaper_V3.png',
    'canvas 380':     'https://storage.googleapis.com/static-mahattaart-online/surface/Canvas%20380%20Gsm%20Wallpaper_V3.png',
    'premium canvas': 'https://storage.googleapis.com/static-mahattaart-online/surface/Canvas%20380%20Gsm%20Wallpaper_V3.png',
    'non woven':      'https://storage.googleapis.com/static-mahattaart-online/surface/Non%20Woven%20wallpaper_V3.png',
    'non-woven':      'https://storage.googleapis.com/static-mahattaart-online/surface/Non%20Woven%20wallpaper_V3.png',
    'glitter':        'https://storage.googleapis.com/static-mahattaart-online/surface/Roadies%20Glitter%20Wallpaper_V2.png',
    'self adhesive':  'https://storage.googleapis.com/static-mahattaart-online/surface/Self%20Adhesiv%20Vinyl%20120%20Wallpaper_V2.png',
    'canvas':         'https://storage.googleapis.com/static-mahattaart-online/surface/Canvas%20220%20Gsm%20Wallpaper_V3.png',
  };

  private getSurfaceImage(paper: any): string {
    if (!paper) return '';
    const name = (paper.display_name || paper.name || '').toLowerCase();
    return this.surfaceImages[name]
      || Object.entries(this.surfaceImages).find(([k]) => name.includes(k))?.[1]
      || '';
  }

  // Surface type specs
  readonly surfaceSpecs: Record<string, { thickness: string; material: string; finish: string; water: string; etc: string }> = {
    'self adhesive': { thickness: '120 MICRON', material: 'Pvc Based', finish: 'Smooth Finish', water: 'Water Proof', etc: 'Odorless, Non Toxic, Cleanable, Sticker' },
    'glitter': { thickness: '240 GSM', material: 'Paper Based', finish: 'Textured, Glittery Shine', water: 'Water Resistant', etc: 'Odorless, Non Toxic' },
    'non-woven': { thickness: '180 GSM', material: 'Paper Based', finish: 'Smooth & Matte', water: 'Water Resistant', etc: 'Odorless, Non Toxic' },
    'non woven': { thickness: '180 GSM', material: 'Paper Based', finish: 'Smooth & Matte', water: 'Water Resistant', etc: 'Odorless, Non Toxic' },
    'canvas': { thickness: '220 GSM', material: 'Polyester Based', finish: 'Fabric Finish', water: 'Water Resistant', etc: 'Odorless, Non Toxic' },
    'premium canvas': { thickness: '380 GSM', material: 'Polyester Based', finish: 'Textured Fabric', water: 'Water Proof', etc: 'Odorless, Non Toxic, Scratch Resistant' },
    'brush strokes': { thickness: '240 GSM', material: 'Pvc Cotton Based', finish: 'Embossed Brush Texture', water: 'Water Resistant', etc: 'Odorless, Non Toxic' },
  };

  get selectedSurfaceSpec(): any {
    if (!this.selectedPaper) return null;
    const name = (this.selectedPaper.display_name || this.selectedPaper.name || '').toLowerCase();
    return this.surfaceSpecs[name] || Object.entries(this.surfaceSpecs).find(([k]) => name.includes(k))?.[1] || null;
  }

  hoveredPaperId: any = null;
  showSampleDetail = false;

  // The kit price rule, read from the server (GET /api/site/samples/pricing) so
  // the page shows the same figure the cart bills. These only cover the instant
  // before it answers; the cart is priced by the server either way.
  SAMPLE_BASE_PRICE = 199;
  SAMPLE_EXTRA_PRICE = 50;
  addingSample = false;

  private loadSamplePricing() {
    this.state.apiGetSamplePricing().subscribe({
      next: (res: any) => {
        const p = res?.data ?? res ?? {};
        if (p.base_price != null) this.SAMPLE_BASE_PRICE = Number(p.base_price);
        if (p.extra_price != null) this.SAMPLE_EXTRA_PRICE = Number(p.extra_price);
      },
      error: () => {},
    });
  }

  get sampleTotalPrice(): number {
    const count = this.selectedSampleSurfaceIds.size;
    if (count === 0) return this.SAMPLE_BASE_PRICE;
    return this.SAMPLE_BASE_PRICE + (count - 1) * this.SAMPLE_EXTRA_PRICE;
  }

  orderSample() {
    if (this.selectedSampleSurfaceIds.size === 0 || this.addingSample) return;
    const wp = this.wallpaper;
    if (!wp) return;
    const paperIds = Array.from(this.selectedSampleSurfaceIds).map(id => Number(id));

    this.addingSample = true;
    this.state.apiAddSampleToCart({
      wallpaper_id: Number(wp.id),
      paper_ids: paperIds,
    }).subscribe({
      next: () => {
        this.addingSample = false;
        this.state.showToast('Sample kit added to cart!', 'cart');
        this.state.loadCartFromApi(true);
        this.router.navigate(['/cart']);
      },
      error: (err: any) => {
        this.addingSample = false;
        const msg = err?.error?.message || 'Failed to add sample. Please try again.';
        this.state.showToast(msg, 'info');
      }
    });
  }
  showExpertDetail = false;
  showInstallDetail = false;
  // Below the `lg` breakpoint (1024px) the purchase panel is stacked full-width instead of
  // a side column, so the "Order a Sample" panel should expand inline rather than replace the
  // rest of the panel. Kept in sync via the window:resize HostListener below.
  isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
  bookingConsultation = false;
  showExpertTerms = false;
  expertPincode = '';
  expertPincodeChecking = false;
  expertPincodeMessage = '';
  expertPincodeAvailable = false;
  wantExpertVisit = false;
  expertVisitBooked = false;

  /**
   * The `cart` row id the visit booking created, from the add-to-cart response's
   * `cart_ids`. Kept so deselecting the visit can remove that exact line instead
   * of reloading the cart and guessing which entry was the consultation.
   */
  expertVisitCartId: number | null = null;

  readonly surfaceExtra: Record<string, { tagline: string; install: string; price: string }> = {
    'self adhesive': { tagline: 'No installation needed. Peel, stick, done.', install: 'Self-install friendly', price: 'Economy pricing' },
    'glitter': { tagline: 'Subtle sparkle under light. Eye-catching accent walls.', install: 'Professional install recommended', price: 'Specialty pricing' },
    'non-woven': { tagline: 'Most popular. Easy to install, easy to reposition.', install: 'Professional install recommended', price: 'Mid price' },
    'non woven': { tagline: 'Most popular. Easy to install, easy to reposition.', install: 'Professional install recommended', price: 'Mid price' },
    'canvas': { tagline: 'Fabric-like texture. Warm, rich finish for any room.', install: 'Professional install recommended', price: 'Mid price' },
    'premium canvas': { tagline: 'Heavy-duty. Scratch-resistant. Statement walls.', install: 'Professional install recommended', price: 'Premium pricing' },
    'brush strokes': { tagline: 'Embossed texture. Looks like hand-painted canvas.', install: 'Professional install recommended', price: 'Specialty pricing' },
  };

  get selectedSurfaceExtra(): { tagline: string; install: string; price: string } | null {
    if (!this.selectedPaper) return null;
    const name = (this.selectedPaper.display_name || this.selectedPaper.name || '').toLowerCase();
    return this.surfaceExtra[name] || Object.entries(this.surfaceExtra).find(([k]) => name.includes(k))?.[1] || null;
  }

  get surfaceHoverImage(): string {
    return this.getSurfaceImage(this.selectedPaper);
  }

  getPaperImage(paper: any): string {
    return this.getSurfaceImage(paper);
  }


  // Size
  selectedSizeType = 'CUSTOM';
  readonly sizeTypes = ['CUSTOM', 'SAMPLE', 'STANDARD'];
  selectedSampleOption = 'surface-kit';
  selectedSampleSurfaceIds = new Set<any>();

  toggleSampleSurface(paper: any) {
    if (!paper) return;
    if (this.selectedSampleSurfaceIds.has(paper.id)) {
      this.selectedSampleSurfaceIds.delete(paper.id);
    } else {
      this.selectedSampleSurfaceIds.add(paper.id);
    }
  }

  isSampleSurfaceSelected(paper: any): boolean {
    return paper && this.selectedSampleSurfaceIds.has(paper.id);
  }

  // Standard sizes — loaded from wallpaper.standard_size1..4
  selectedStandardSize: { label: string; w: number; h: number; area: number } = { label: '10 × 10 ft', w: 10, h: 10, area: 100 };

  get standardSizes(): { label: string; w: number; h: number; area: number }[] {
    if (!this.wallpaper) return [];
    const sizes: { label: string; w: number; h: number; area: number }[] = [];
    ['standard_size1', 'standard_size2', 'standard_size3', 'standard_size4'].forEach(key => {
      const val = this.wallpaper[key];
      if (!val) return;
      const parsed = this.parseStandardSize(String(val));
      if (parsed) sizes.push(parsed);
    });
    return sizes;
  }

  // standard_size1 (in feet) — caps user-entered custom dimensions
  get maxCustomDims(): { wFt: number; hFt: number } | null {
    if (!this.wallpaper) return null;
    const val = this.wallpaper['standard_size1'];
    if (!val) return null;
    const parsed = this.parseStandardSize(String(val));
    if (!parsed) return null;
    return { wFt: parsed.w, hFt: parsed.h };
  }

  private parseStandardSize(val: string): { label: string; w: number; h: number; area: number } | null {
    // backend sends inches e.g. "180x120" → convert to feet
    const match = val.replace(/×/g, 'x').match(/(\d+(?:\.\d+)?)\s*[xX]\s*(\d+(?:\.\d+)?)/);
    if (!match) return null;
    const wIn = parseFloat(match[1]);
    const hIn = parseFloat(match[2]);
    if (!wIn || !hIn) return null;
    const w = parseFloat((wIn / 12).toFixed(2));
    const h = parseFloat((hIn / 12).toFixed(2));
    const area = Math.ceil(w * h);
    return { label: `${w} × ${h} ft`, w, h, area };
  }

  private initStandardSize() {
    const sizes = this.standardSizes;
    if (sizes.length > 0) this.selectedStandardSize = sizes[0];
  }

  // Wall calculator (multi-wall) — widthFt/In and heightFt/In are UI fields; width/height are computed decimal feet
  walls: { width: number | null; height: number | null; unit: string; widthFt?: number; widthIn?: number; heightFt?: number; heightIn?: number }[] = [{ width: null, height: null, unit: 'ft', widthFt: undefined, widthIn: undefined, heightFt: undefined, heightIn: undefined }];

  // True when any wall has any dimension filled — used to gate the "Add wall" + button
  get hasAnyWallDims(): boolean {
    return this.walls.some(w => !!(w.widthFt || w.widthIn || w.heightFt || w.heightIn));
  }
  wallWidth = '';
  wallHeight = '';
  wallUnit = 'ft';
  calculatedPrice: number | null = null;
  priceLoading = false;
  private dimensionTimer: any = null;
  /**
   * A dimension edit is waiting out the debounce and WILL be quoted. With
   * `priceLoading` it drives the inline "Updating…" state, so the last valid
   * price stays on screen while the new one is worked out.
   */
  quotePending = false;
  /** Which walls (indexes into `walls`) the price on screen was quoted for. */
  pricedWallIdx: number[] = [];
  /**
   * What the server refused in the last quote: a message per wall (shown under
   * that wall, and Add to Cart is blocked) and anything general.
   */
  quoteErrors: PdpQuoteErrors = NO_QUOTE_ERRORS;
  // The summary is derived from `pricingData` and cached on its identity, so
  // every path that replaces or clears `pricingData` updates it with no extra
  // bookkeeping — and change detection does not re-map it on every check.
  private summarySource: any = undefined;
  private summaryCache: PdpQuoteSummary | null = null;
  // Monotonic id for each /quote request. calculatePrice() fires from ~11 places
  // with no cancellation, so two requests can be in flight at once; only the
  // LATEST one may write pricingData, or a slower older response overwrites the
  // newer price while the dimensions (read from this.walls) stay current.
  private calcSeq = 0;
  // The /quote request in flight. A newer one CANCELS it rather than letting it
  // run to completion and be thrown away by the calcSeq guard — while the
  // customer types across walls, the old answers were still queuing up ahead of
  // the one that matters.
  private quoteReq: Subscription | null = null;
  // How long typing must pause before a quote goes out. The quote itself answers
  // in ~150 ms (one wall or five), so this wait was most of what the customer
  // felt: 800 ms after EVERY keystroke, across ft + in on every wall.
  private static readonly QUOTE_DEBOUNCE_MS = 350;

  /**
   * Smallest wallpaper order the site accepts, sq ft summed over every wall —
   * the backend's setting, sent with the product. 0 (not sent) = no check here;
   * the cart enforces it either way.
   */
  get minOrderSqft(): number {
    return Number(this.wallpaper?.min_order_sqft) || 0;
  }

  /**
   * Most walls one quote / add-to-cart takes — the backend's request-size
   * safeguard, sent with the product. Null (not sent, or 0) = no limit here; the
   * server validates every request either way.
   */
  get maxWalls(): number | null {
    const max = Number(this.wallpaper?.max_walls);
    return max > 0 ? max : null;
  }

  get canAddWall(): boolean {
    return this.maxWalls === null || this.walls.length < this.maxWalls;
  }

  // Installation
  pincode = '';
  pincodeMessage = '';
  pincodeAvailable = false;
  pincodeChecking = false;
  wantInstallation = false;
  wantDelivery = true;
  /**
   * Installation ₹/sq ft for the checked pincode — set from the pincode response,
   * never assumed.
   *
   * It used to be initialised to a hardcoded 20 and only overwritten if the old
   * customization endpoint happened to send `perSqFtInstallation`. Production sets
   * this per CITY in the Installation Rates panel (Ghaziabad is 13), so a constant
   * here was wrong everywhere except by coincidence. 0 means "not known yet" — the
   * page shows no rate until a pincode is checked, which is honest.
   */
  installationRate = 0;

  /**
   * The fixed installation charge and the TOTAL wall area it covers, from the same
   * pincode check (Production → Installation Rates → Fixed installation charge).
   * All walls of the order are added up: ≤ installationUptoSqft → the fixed amount,
   * above it total area × installationRate. Null = no fixed band configured.
   */
  installationFixedAmount: number | null = null;
  installationUptoSqft: number | null = null;
  /** "How is installation charged?" popup. */
  showInstallInfo = false;

  /**
   * Expert-visit fee for the checked pincode, from the Measurement Service panel.
   * Was a literal ₹999 in the template; it is per city and Production can change
   * it, so it comes down with the pincode like the installation rate does.
   */
  expertVisitFee = 0;

  /**
   * The expert-visit POLICY, from GET /api/site/service/visit-terms.
   *
   * `min_order_value` and `validity_months` were literals — "₹4,999" and "6
   * months" — written twice in the template and once more inside the booking
   * payload's caption. Three copies of one business rule, none of which
   * Production could change without a frontend deploy. `fee_from` is the global
   * fee, so the accordion can quote a figure before a pincode is checked; once
   * one is, `expertVisitFee` (the city's own) takes over.
   *
   * Null until it loads, and every consumer renders nothing rather than a
   * placeholder number — a wrong promise is worse than a missing one.
   */
  visitTerms: { fee_from: number | null; min_order_value: number; validity_months: number } | null = null;

  showInstallationModal = false;
  installationAgreed = false;

  // Pricing from API
  pricingData: any = null;
  noOfRolls = 0;
  private lastPrintRate: number | null = null; // stable rate for "Starts at" display
  // No GST constant here any more, deliberately. A surface's slab lives on its
  // paper row and differs per surface (18% on one, 30% on another), so a rate
  // hardcoded in the client can only be wrong for some of them — and was, by 9.2%.
  // Every tax-inclusive figure now arrives already computed from the server.
  //
  // FLAT_DISCOUNT is gone for the same reason: it was 0, so it did nothing except
  // sit there as a second place a price could be quietly altered.

  private sub = new Subscription();

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    public state: AppStateService,
    private auth: AuthService,
    private wallpaperService: WallpaperService,
    private dataLayer: DataLayerService,
    private triggerService: TriggerService,
  ) { }

  ngOnInit() {
    this.loadSamplePricing();
    this.sub.add(this.state.wishlistIds$.subscribe(ids => this.wishlistIds = ids));
    this.sub.add(this.state.context$.subscribe(c => this.context = c));
    // Load master colors (hex values) then resolve wallpaper colors
    this.wallpaperService.getMasters().subscribe({
      next: (res: MastersResponse) => {
        this.masterColors = res.color || [];
        this.resolveColors();
      },
      error: () => { }
    });

    // Expert-visit terms. Loaded on init because the accordion prints them before
    // any pincode is entered, and left null on failure so the terms line hides
    // rather than quoting a number this build happens to remember.
    this.wallpaperService.getExpertVisitTerms().subscribe({
      next: (res: any) => {
        this.visitTerms = res || null;
        // Quote the global fee until a pincode replaces it with the city's own.
        if (!this.expertVisitFee && res?.fee_from) this.expertVisitFee = Number(res.fee_from);
      },
      error: () => { }
    });

    this.sub.add(this.route.params.subscribe(params => {
      const slugOrId = params['slug'] || params['id'];
      // Don't reload if same wallpaper
      if (slugOrId === this.currentSlug && this.wallpaper) return;
      this.currentSlug = slugOrId;
      this.loadWallpaper(slugOrId);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }));

    // Smart triggers — start watching this PDP visit
    this.triggerService.markPdpVisited();
    this.triggerService.pdpStartSizeWatch(() => ({
      widthFilled: !!parseFloat(this.wallWidth),
      heightFilled: !!parseFloat(this.wallHeight)
    }));
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.quoteReq?.unsubscribe();
    clearTimeout(this.dimensionTimer);
    document.body.style.overflow = '';
    clearTimeout(this.hesitationTimer);
    this.triggerService.pdpStopSizeWatch();
    this.triggerService.pdpAddCartHoverEnd();
  }

  // T4 — Add to Cart hover handlers (wire from template via mouseenter/mouseleave)
  onAddCartHoverIn() { this.triggerService.pdpAddCartHoverStart(); }
  onAddCartHoverOut() { this.triggerService.pdpAddCartHoverEnd(); }

  // C19 — hesitation timer (fires at 60s after price shown with < 3 actions)
  private startPdpHesitationTimer(): void {
    clearTimeout(this.hesitationTimer);
    this.hesitationTimer = setTimeout(() => {
      if (this.pdpActionCount < 3 && !this.addToCartAttempted) {
        const productId = this.wallpaper?.wallpaper_id || String(this.wallpaper?.id || '');
        this.dataLayer.pushPdpHesitation({
          productId,
          timeSpent: Math.round((Date.now() - this.pdpEnterTime) / 1000),
          actionsTaken: this.pdpActionCount,
          priceShown: this.previewGenerated,
          customizationDone: this.wallInputStarted
        });
      }
    }, 60000);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.isMobile = window.innerWidth < 1024;
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(e: KeyboardEvent): void {
    if (!this.isLightboxOpen) return;
    if (e.key === 'Escape') this.closeLightbox();
    if (e.key === 'ArrowRight') this.nextImage();
    if (e.key === 'ArrowLeft') this.prevImage();
  }

  openLightbox() {
    this.isLightboxOpen = true;
    document.body.style.overflow = 'hidden';
  }

  closeLightbox() {
    this.isLightboxOpen = false;
    document.body.style.overflow = '';
  }

  // C20 — exit intent (mouse leaves viewport top)
  @HostListener('document:mouseleave', ['$event'])
  onMouseLeave(e: MouseEvent): void {
    if (e.clientY <= 0 && !this.exitIntentFired && this.wallpaper) {
      this.exitIntentFired = true;
      this.dataLayer.pushPdpExitIntent({
        productId: this.wallpaper.wallpaper_id || String(this.wallpaper.id),
        timeSpent: Math.round((Date.now() - this.pdpEnterTime) / 1000),
        customizationDone: this.wallInputStarted,
        priceSeen: this.previewGenerated,
        addToCartAttempted: this.addToCartAttempted
      });
    }
  }

  // ── Loading ────────────────────────────────────────────────────
  private loadWallpaper(slugOrId: string) {
    this.isLoading = true;
    this.loadError = false;
    this.wallpaper = null;
    this.papers = [];
    this.selectedPaper = null;
    this.papersLoaded = false;
    this.similarWallpapers = [];
    this.calculatedPrice = null;
    this.activeImageIndex = 0;
    this.selectedColorIndex = 0;
    this.isZoomed = false;
    this.activeTab = 'specs';
    this.pricingData = null;
    this.noOfRolls = 0;
    this.resolvedColors = [];
    this.walls = [{ width: null, height: null, unit: 'ft', widthFt: undefined, widthIn: undefined, heightFt: undefined, heightIn: undefined }];
    // The SERVICE selection has to go too. Only the size was being reset here, so
    // installation stayed selected across products: the next design opened with
    // `wantInstallation` still true and no verified pincode behind it, which hid the
    // pincode form (it was gated on `!wantInstallation`) and left the accordion
    // showing nothing at all.
    this.resetServiceSelection();

    // Try backend first, fallback to search API on failure
    this.wallpaperService.initWallpaperCustomization(slugOrId).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        const wp = data?.wallpaper || (data?.id ? data : null);

        if (wp) {
          this.wallpaper = wp;
          if (data.perSqFtInstallation) this.installationRate = data.perSqFtInstallation;
          if (data.product) this.product = data.product;
          if (data.productType) this.productType = data.productType;
          if (data.spaceLabels) this.spaceLabels = data.spaceLabels;
          this.isLoading = false;
          this.resolveColors();
          this.initStandardSize();
          // Restore the saved walls/size/services BEFORE loading papers. getAllPapers()
          // is a PRELOADED subject that emits SYNCHRONOUSLY on a return visit, and
          // loadAllPapers() prices off the CURRENT walls the instant it emits. If the
          // restore ran after it (as it used to), that emit saw an empty wall — area 0,
          // so it took the "starting rate" branch instead of quoting — and the restored
          // dimensions were shown with a dash and never priced until the customer nudged
          // a size. Restoring first makes that synchronous emit price the real size.
          this.restorePdpConfig();
          this.loadAllPapers();
          this.loadSimilar();
          this.pushWallpaperGTM();
        } else {
          this.loadWallpaperFromSearch(slugOrId);
        }
      },
      error: () => this.loadWallpaperFromSearch(slugOrId)
    });
  }

  private loadWallpaperFromSearch(slugOrId: string) {
    const searchText = slugOrId.replace(/-/g, ' ');
    this.wallpaperService.searchWallpapers({ searchtext: searchText, images: 10, page: 1 }).subscribe({
      next: (res) => {
        const results = res.results || [];
        const match = results.find(
          (w: SearchWallpaper) => w.slug === slugOrId || String(w.id) === slugOrId || w.wallpaper_id === slugOrId
        ) || results[0];
        if (match) {
          this.wallpaper = {
            ...match,
            image: match.list_thumbnail,
            name: match.name,
            price: match.price,
            collection_name: match.collection_slug,
            category_id: match.collection,
          };
          this.isLoading = false;
          this.resolveColors();
          this.initStandardSize();
          // Restore before loading papers — see the note in loadWallpaper(): the
          // preloaded papers subject emits synchronously, so the saved size must be in
          // place before loadAllPapers() prices it, or a return visit shows a dash.
          this.restorePdpConfig();
          this.loadAllPapers();
          this.loadSimilar();
          this.pushWallpaperGTM();
        } else {
          this.loadError = true;
          this.isLoading = false;
        }
      },
      error: () => { this.loadError = true; this.isLoading = false; }
    });
  }

  // GTM tracking state
  private pdpEnterTime = 0;
  private previewGenerated = false;
  private previewChangeCount = 0;
  private lastPreviewPrice = 0;
  private wallInputStarted = false;
  private lastMockupChangeTime = 0;
  private lastSpaceLabel = '';
  private pdpActionCount = 0;
  private addToCartAttempted = false;
  private exitIntentFired = false;
  private hesitationTimer: any;

  private pushWallpaperGTM(): void {
    if (!this.wallpaper) return;
    const wp = this.wallpaper;
    const productId = wp.wallpaper_id || String(wp.id);
    const category = wp.category_name || wp.collection_name || wp.collection_slug || wp.category || this.context.selected_category || '';

    this.dataLayer.setPageTitle(wp.name || 'Wallpaper');
    this.pdpEnterTime = Date.now();
    this.previewGenerated = false;
    this.previewChangeCount = 0;
    this.wallInputStarted = false;
    this.lastMockupChangeTime = Date.now();
    this.lastSpaceLabel = this.context.selected_space || '';
    this.pdpActionCount = 0;
    this.addToCartAttempted = false;
    this.exitIntentFired = false;
    this.startPdpHesitationTimer();

    // C1 — view_item (GA4)
    this.dataLayer.pushViewItem({
      item_id: productId,
      item_name: wp.name,
      // `perSqFtDisplay`, not `wp.price`: the rate actually on screen. When the
      // detail endpoint answered they are the same figure, but on the Solr fallback
      // path `wp.price` is now null — the service drops Solr's per-design snapshot —
      // and reporting null where a live rate is known would lose the attribution.
      price: this.perSqFtDisplay || null,
      item_category: category,
      item_variant: wp.wallpaper_type || '',
    });

    // Store PDP context for WhatsApp click tracking
    try {
      sessionStorage.setItem('current_pdp_context', JSON.stringify({
        product_id:   productId,
        product_name: wp.name || '',
        category
      }));
    } catch {}

    // C1b — pdp_view (custom)
    const hasMockup = !!(wp.space1 || wp.space2 || wp.space3);
    this.dataLayer.pushPdpView({
      productId,
      productName: wp.name,
      category,
      sourceFunnel: this.context.search_query ? 'search'
        : this.context.selected_space ? 'plp'
        : this.context.selected_category ? 'plp'
        : 'direct',
      spaceContext: this.context.selected_space || null,
      // `perSqFtDisplay`, not `wp.price`: the rate actually on screen. When the
      // detail endpoint answered they are the same figure, but on the Solr fallback
      // path `wp.price` is now null — the service drops Solr's per-design snapshot —
      // and reporting null where a live rate is known would lose the attribution.
      price: this.perSqFtDisplay || null,
      hasMockup,
    });
  }

  selectedVariantImage: string = '';
  variantColors: { hex: string; image: string; data: any }[] = [];
  variantAnimating = false;

  // private resolveColors() {
  //   if (!this.wallpaper) return;
  //   const wpColors = this.wallpaper.colors || [];

  //   // If colors already have hex, use directly
  //   if (wpColors.length > 0 && wpColors[0]?.hex) {
  //     this.resolvedColors = wpColors.map((c: any) => ({ name: c.name || '', hex: c.hex }));
  //     return;
  //   }

  //   // Match color IDs or names against masterColors
  //   if (wpColors.length > 0 && this.masterColors.length > 0) {
  //     const matched = wpColors
  //       .map((c: any) => {
  //         const id = typeof c === 'number' ? c : c?.id;
  //         if (id) return this.masterColors.find(mc => mc.id === id);
  //         const name = typeof c === 'string' ? c : c?.name;
  //         if (name) return this.masterColors.find(mc => mc.name?.toLowerCase() === name.toLowerCase());
  //         return null;
  //       })
  //       .filter((c: any): c is MasterItem => !!(c && c.hex));
  //     if (matched.length > 0) {
  //       this.resolvedColors = matched.map(c => ({ name: c.name, hex: c.hex! }));
  //       return;
  //     }
  //   }

  //   // Fallback: max_color / min_color
  //   const fallback: { name: string; hex: string }[] = [];
  //   if (this.wallpaper.max_color) {
  //     const match = this.masterColors.find(mc => mc.name?.toLowerCase() === this.wallpaper.max_color.toLowerCase());
  //     fallback.push({ name: this.wallpaper.max_color, hex: match?.hex || this.colorNameToHex(this.wallpaper.max_color) });
  //   }
  //   if (this.wallpaper.min_color && this.wallpaper.min_color !== this.wallpaper.max_color) {
  //     const match = this.masterColors.find(mc => mc.name?.toLowerCase() === this.wallpaper.min_color.toLowerCase());
  //     fallback.push({ name: this.wallpaper.min_color, hex: match?.hex || this.colorNameToHex(this.wallpaper.min_color) });
  //   }
  //   this.resolvedColors = fallback;
  // }


  private resolveColors() {
    if (!this.wallpaper) return;

    // Handle colors_arr variants
    if (Array.isArray(this.wallpaper.colors_arr) && this.wallpaper.colors_arr.length > 0) {

      this.variantColors = this.wallpaper.colors_arr.map((variant: any) => {
        const palette = variant.color_palate?.split(',') || [];
        let firstColor = (palette[0] || '').trim();
        // Ensure proper hex format
        if (firstColor && /^[0-9a-fA-F]{3,6}$/.test(firstColor)) {
          firstColor = '#' + firstColor;
        }
        // Use variant thumbnail for the circle preview
        const thumb = variant.list_thumbnail || variant.pattern || variant.space1 || '';

        return {
          hex: firstColor || '#ccc',
          thumb,
          image: variant.pattern || variant.space1 || variant.list_thumbnail,
          data: variant
        };
      });

      // default select first variant
      this.selectedColorIndex = 0;

      // default image set
      this.activeImageIndex = 0;
      // this.selectedVariantImage = this.variantColors[0]?.image;

      return;
    }

    // fallback old logic
  }

  changeVariant(index: number) {
    this.selectedColorIndex = index;
    this.selectedVariantImage = '';
    this.activeImageIndex = 0;
    // C3 — image_interact (color swatch = thumbnail_click)
    this.dataLayer.pushImageInteract({
      productId: this.wallpaper?.wallpaper_id || String(this.wallpaper?.id || ''),
      action: 'thumbnail_click',
      imageIndex: index,
      totalImages: this.variantColors.length
    });
    this.savePdpConfig();
  }

  // ── Image navigation ──────────────────────────────────────
  prevImage(): void {
    const newIdx = this.activeImageIndex > 0 ? this.activeImageIndex - 1 : this.images.length - 1;
    this.changeActiveImage(newIdx);
  }

  nextImage(): void {
    const newIdx = this.activeImageIndex < this.images.length - 1 ? this.activeImageIndex + 1 : 0;
    this.changeActiveImage(newIdx);
  }

  scrollThumbs(dir: 'left' | 'right'): void {
    const el = this.thumbStripEl?.nativeElement;
    if (!el) return;
    const scrollAmount = dir === 'left' ? -300 : 300;
    el.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }

  changeActiveImage(index: number) {
    // C4 — detect mockup/space change
    const prevImg = this.images[this.activeImageIndex] || '';
    const newImg = this.images[index] || '';
    const prevSpace = this.getSpaceLabel(prevImg);
    const newSpace = this.getSpaceLabel(newImg);
    if (prevSpace && newSpace && prevSpace !== newSpace) {
      this.dataLayer.pushMockupViewChange({
        productId: this.wallpaper?.wallpaper_id || String(this.wallpaper?.id || ''),
        spaceFrom: prevSpace,
        spaceTo: newSpace,
        timeToChange: Math.round((Date.now() - this.lastMockupChangeTime) / 1000)
      });
      this.lastMockupChangeTime = Date.now();
    }

    this.selectedVariantImage = '';
    this.activeImageIndex = index;
    // T3 — record thumbnail cycle (3+ → mockup request)
    this.triggerService.pdpRecordThumbClick();
    // C3 — image_interact
    this.dataLayer.pushImageInteract({
      productId: this.wallpaper?.wallpaper_id || String(this.wallpaper?.id || ''),
      action: 'thumbnail_click',
      imageIndex: index,
      totalImages: this.images.length
    });
  }

  /** Map an image URL to a space label for C4 mockup tracking */
  private getSpaceLabel(imgUrl: string): string {
    if (!this.wallpaper || !imgUrl) return '';
    const wp = this.wallpaper;
    const labels = this.spaceLabels || {};
    if (wp.space1 === imgUrl) return labels['space1'] || 'space1';
    if (wp.space2 === imgUrl) return labels['space2'] || 'space2';
    if (wp.space3 === imgUrl) return labels['space3'] || 'space3';
    if (wp.space4 === imgUrl) return labels['space4'] || 'space4';
    if (wp.space5 === imgUrl) return labels['space5'] || 'space5';
    if (wp.space6 === imgUrl) return labels['space6'] || 'space6';
    return '';
  }

  papersLoaded = false;

  private loadAllPapers() {
    this.wallpaperService.getAllPapers().subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        this.papers = Array.isArray(data) ? data : [];
        if (this.papers.length > 0) {
          // The SERVER says which surface is the default (`is_default`, resolved
          // from `sales.default_surface`). This used to match `'non-woven'` on
          // `display_name` here, which was wrong twice over: the name is not unique
          // — a PRINT & FRAME paper is also called "Non-Woven" (id 4, ₹72) — and the
          // listing card's rate came from a different rule entirely, so the page
          // opened on a surface the card had not quoted.
          //
          // Falls back to the cheapest PRICED surface, then to the first row, so a
          // payload without the flag still lands somewhere sensible.
          this.selectedPaper =
            this.papers.find((p: any) => p.is_default) ||
            this.papers
              .filter((p: any) => p.rate != null && p.rate > 0)
              .sort((a: any, b: any) => a.rate - b.rate)[0] ||
            this.papers[0];
          this.restorePaperFromDraft();
          // A restored service choice re-verifies its pincode and re-quotes itself,
          // so it must not also be priced here — that would fire two quotes and let
          // the one WITHOUT the service land second.
          if (this.wantInstallation || this.wantExpertVisit) {
            this.rehydrateServices();
          } else if (this.selectedSizeType === 'STANDARD' || this.selectedSizeType === 'SAMPLE' || this.totalWallArea > 0) {
            // Auto-calculate with selected paper for any size type that has dimensions
            this.calculatePrice();
          } else {
            this.fetchStartingRate();
          }
        }
        this.papersLoaded = true;
      },
      error: () => { this.papersLoaded = true; }
    });
  }

  private loadSimilar() {
    const wp = this.wallpaper;
    if (!wp) return;

    // The rail can take three requests to settle (scored endpoint → category
    // search → name search), so it stays "loading" until one of them produces
    // results or all of them have given up. Without this the section simply did
    // not exist for a second or two and then appeared, pushing the page down.
    this.similarLoading = true;

    const slugParam = wp.slug || '';
    const wpIdParam = wp.wallpaper_id || '';
    const idParam   = wp.id;

    // Use the scored backend endpoint that ranks by keyword + category + theme match
    this.wallpaperService.getSimilarWallpapers(
      { slug: slugParam || undefined, wallpaper_id: wpIdParam || undefined, id: idParam },
      12
    ).subscribe({
      next: (res: any) => {
        const results = (res.data || []).slice(0, this.similarCount);
        if (results.length > 0) {
          this.similarWallpapers = results;
          this.similarLoading = false;
          this.dataLayer.pushRecommendationView({
            type: 'similar',
            productIds: results.map((w: any) => w.wallpaper_id || String(w.id)),
            sourceProductId: wp.wallpaper_id || String(wp.id || '')
          });
        } else {
          this.loadSimilarFallback();
        }
      },
      error: () => this.loadSimilarFallback()
    });
  }

  private loadSimilarFallback() {
    const wp = this.wallpaper;
    if (!wp) { this.similarLoading = false; return; }
    const categoryId = wp.category_id || wp.collection;
    const wpName     = wp.name || '';

    if (categoryId) {
      this.wallpaperService.searchWallpapers({
        category: String(categoryId),
        images: 7,
        page: 1,
        sortby: 'id',
        sortdir: 'desc',
      }).subscribe({
        next: (res) => {
          // Prices on these already come from the live chain — the service
          // overrides Solr's on the way out, for every caller.
          const results = (res.results || []).filter((w: any) => w.id !== wp.id).slice(0, this.similarCount);
          if (results.length > 0) {
            this.similarWallpapers = results;
            this.similarLoading = false;
            this.dataLayer.pushRecommendationView({
              type: 'similar',
              productIds: results.map((w: any) => w.wallpaper_id || String(w.id)),
              sourceProductId: wp.wallpaper_id || String(wp.id || '')
            });
          } else if (wpName) {
            this.loadSimilarByName(wpName);
          } else {
            this.similarLoading = false;
          }
        },
        error: () => this.loadSimilarByName(wpName)
      });
    } else if (wpName) {
      this.loadSimilarByName(wpName);
    } else {
      this.similarLoading = false;
    }
  }

  private loadSimilarByName(name: string) {
    // Last link in the chain: whether it runs or not, the rail has stopped loading.
    if (!name) { this.similarLoading = false; return; }
    this.wallpaperService.searchWallpapers({
      searchtext: name,
      images: 7,
      page: 1,
    }).subscribe({
      next: (res) => {
        const currentId = this.wallpaper?.id;
        this.similarWallpapers = (res.results || []).filter((w: any) => w.id !== currentId).slice(0, this.similarCount);
        this.similarLoading = false;
        if (this.similarWallpapers.length > 0) {
          this.dataLayer.pushRecommendationView({
            type: 'similar',
            productIds: this.similarWallpapers.map((w: any) => w.wallpaper_id || String(w.id)),
            sourceProductId: this.wallpaper?.wallpaper_id || String(this.wallpaper?.id || '')
          });
        }
      },
      error: () => { this.similarLoading = false; }
    });
  }

  private fetchStartingRate() {
    if (!this.wallpaper || !this.selectedPaper) return;
    // Show loading state only on the very first fetch — once we have a rate,
    // keep showing it during subsequent paper changes (avoids flicker to raw fallback).
    const isFirstFetch = !this.lastPrintRate;
    if (isFirstFetch) this.priceLoading = true;
    const params = {
      id: this.wallpaper.id,
      paper: this.selectedPaper.id,
      width: 1, height: 1, sizeType: 2,
      installation_pincode: '', selectedKit: 'sample',
    };
    this.wallpaperService.calculateCustomWallpaperPrice(params).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        if (data?.printRate) this.lastPrintRate = data.printRate;
        if (isFirstFetch) this.priceLoading = false;
      },
      error: () => { if (isFirstFetch) this.priceLoading = false; }
    });
  }

  // ── Price Calculation (calculateRolls equivalent) ─────────────
  // Sample size fixed at 8" x 11" (from environment.sampleSize)
  private readonly SAMPLE_WIDTH = 8;
  private readonly SAMPLE_HEIGHT = 11;

  calculatePrice() {
    if (!this.wallpaper) return;

    const request = this.quoteRequest();
    if (!request) {
      // A custom size with no measured wall yet: the "from" rate instead.
      if (this.selectedSizeType === 'CUSTOM') this.fetchStartingRate();
      return;
    }
    const { params, quotedIdx, width, height, sizeType } = request;
    // What this request asked, word for word — the price it brings back is shown
    // only while the page would still ask exactly this (quoteIsCurrent).
    const signature = JSON.stringify(params);

    this.priceLoading = true;
    const calcId = ++this.calcSeq;

    this.quoteReq?.unsubscribe();
    this.quoteReq = this.wallpaperService.calculateCustomWallpaperPrice(params).subscribe({
      next: (res: any) => this.onQuoteLoaded(res, calcId, signature, quotedIdx, width, height, sizeType),
      error: (err: any) => this.onQuoteFailed(err, calcId, quotedIdx),
    });
  }

  /**
   * The quote request for what is on screen now, or null when there is nothing
   * to price (no measured wall). Shared by calculatePrice and quoteIsCurrent, so
   * "the walls this price is for" and "the walls on screen" are one comparison.
   */
  private quoteRequest(): { params: Record<string, any>; quotedIdx: number[]; width: number; height: number; sizeType: number } | null {
    if (!this.wallpaper) return null;

    let width = 0;
    let height = 0;
    let sizeType = 2; // default custom
    // Every measured wall, for a custom size. The server cuts each wall on its own
    // and charges installation ONCE on their combined area — the same way the cart
    // writes them — so the quote is the whole job, not just the first wall.
    let quoteWalls: Array<{ width: number; height: number }> | null = null;
    // The walls this request covers, by index — so the summary can label each
    // wall's line once the quote lands (a half-filled wall is not among them).
    let quotedIdx: number[] = [];

    if (this.selectedSizeType === 'SAMPLE') {
      // Sample: fixed size 8"x11", sizeType=3
      width = this.SAMPLE_WIDTH;
      height = this.SAMPLE_HEIGHT;
      sizeType = 3;
    } else if (this.selectedSizeType === 'STANDARD') {
      width = this.selectedStandardSize.w;
      height = this.selectedStandardSize.h;
      sizeType = 1;
    } else {
      // The quote endpoint prices one wall from its actual width and height.
      // Sending sqrt(total area) changes the cut length and therefore the paper
      // consumption; the cart endpoint already sends the real wall dimensions.
      const toFt = (v: number | null, unit: string) =>
        unit === 'in' ? (v || 0) / 12 : unit === 'cm' ? (v || 0) * 3.28 / 100 : v || 0;
      // A wall still being typed (no area yet) is left out rather than failing
      // the whole quote.
      const measured = this.walls.filter(w => this.wallAreaInSqFt(w) > 0);
      quotedIdx = this.walls.map((_, i) => i).filter(i => this.wallAreaInSqFt(this.walls[i]) > 0);
      if (!measured.length) return null;
      quoteWalls = measured.map(w => ({
        width: parseFloat(toFt(w.width, w.unit).toFixed(4)),
        height: parseFloat(toFt(w.height, w.unit).toFixed(4)),
      }));
      width = quoteWalls[0].width;
      height = quoteWalls[0].height;
      sizeType = 2;
    }

    if (width <= 0 || height <= 0) return null;

    const params: Record<string, any> = {
      id: this.wallpaper.id,
      paper: this.selectedPaper?.id || '',
      width,
      height,
      ...(quoteWalls && quoteWalls.length > 1 ? { walls: quoteWalls } : {}),
      sizeType,
      // Sending the pincode is what makes the backend add the installation charge
      // (Helper::getPriceDetail keys off installation_pincode alone), so it must
      // wait for the customer's opt-in — a serviceability check must not bill.
      installation_pincode: (this.pincodeAvailable && this.wantInstallation) ? this.pincode : '',
      selectedKit: this.selectedSampleOption === 'kit-design' ? 'custom' : 'sample',

      // Site services, stated OUTRIGHT rather than inferred from the pincode.
      //
      // The endpoint derives `installation` from a bare pincode when the flag is
      // absent (the old backend's rule, kept so an older bundle still prices), and
      // an expert visit needs a pincode too — so inferring would mark BOTH and the
      // request would be rejected as mutually exclusive. Sending the flags makes
      // the customer's actual choice the thing the server prices, which is also
      // why the visit fee now arrives as `visit_fee` instead of being added on the
      // client.
      installation: this.wantInstallation && !this.isSelfAdhesive,
      expert_visit: this.wantExpertVisit,
      pincode: this.wantExpertVisit
        ? (this.expertPincode || this.pincode)
        : ((this.pincodeAvailable && this.wantInstallation) ? this.pincode : ''),
    };

    return { params, quotedIdx, width, height, sizeType };
  }

  /** The request this price answers (JSON); null when no price is on hand. */
  quotedSignature: string | null = null;

  private onQuoteLoaded(res: any, calcId: number, signature: string, quotedIdx: number[], width: number, height: number, sizeType: number): void {
    const priced = res?.data ?? res;
    // Stale-response guard: if a newer calculation has started since this
    // request went out, its result must NOT overwrite the newer one. Without
    // this, a slower older response repaints a previous price under the
    // current dimensions — the intermittent bug.
    if (calcId !== this.calcSeq) {
      return;
    }
    this.pricingData = priced;
    this.quotedSignature = signature;
    this.pricedWallIdx = quotedIdx;
    this.quoteErrors = NO_QUOTE_ERRORS;
    this.noOfRolls = res?.noOfRolls ?? this.pricingData?.noOfRolls ?? 0;
    if (this.pricingData?.printRate) {
      this.lastPrintRate = this.pricingData.printRate;
    }
    // The override that used to sit here is gone, and deliberately.
    //
    // It recomputed `amount_payable = exactArea × printRate` for standard
    // sizes, on the belief that "the API applies 1.1 waste internally". It does
    // not — verified against the old backend at 10×9, 12×5 and 15×10, where
    // printCost came back as exactly area × rate every time. So the override
    // was correcting for something that never happened, and it did two kinds of
    // harm: `printRate` is EX-TAX, so a standard size displayed a figure ~18%
    // below the inclusive one a custom size showed, and overwriting
    // `amount_payable` with a gross figure silently dropped any discount.
    //
    // The server's numbers now stand for both size types.
    this.priceLoading = false;

    // C9/C10 — preview tracking
    const newPrice = this.pricingData?.amount_payable || 0;
    const productId = this.wallpaper?.wallpaper_id || String(this.wallpaper?.id || '');
    if (!this.previewGenerated) {
      // C9 — first preview_generate
      this.previewGenerated = true;
      this.lastPreviewPrice = newPrice;
      this.dataLayer.pushPreviewGenerate({
        productId,
        width, height, unit: 'ft',
        surfaceType: this.selectedPaper?.display_name || this.selectedPaper?.name || '',
        calculatedPrice: newPrice
      });
    } else {
      // C10 — subsequent preview_change
      this.previewChangeCount++;
      this.dataLayer.pushPreviewChange({
        productId,
        changeType: sizeType === 1 ? 'dimension' : 'dimension',
        oldPrice: this.lastPreviewPrice,
        newPrice,
        changeCount: this.previewChangeCount
      });
      this.lastPreviewPrice = newPrice;
    }
    // C11 — price_update
    const area = this.pricingData?.area_sqft || this.totalWallArea;
    const rate = this.pricingData?.printRate || (area > 0 ? newPrice / area : 0);
    this.dataLayer.pushPriceUpdate({
      productId, width, height, unit: 'ft',
      areaSqft: area, calculatedPrice: newPrice,
      pricePerSqft: Math.round(rate * 100) / 100
    });
    this.pdpActionCount++;
    this.savePdpConfig();
  }

  private onQuoteFailed(err: any, calcId: number, quotedIdx: number[]): void {
    // Same guard on failure: an older request erroring must not wipe the
    // price a newer request has since produced.
    if (calcId !== this.calcSeq) {
      return;
    }
    this.pricingData = null;
    this.quotedSignature = null;
    this.pricedWallIdx = [];
    this.calculatedPrice = null;
    this.priceLoading = false;
    // Say WHICH wall the server refused (its `walls.{n}` is the n-th quoted
    // wall), so the message lands under that wall instead of a blank price.
    this.quoteErrors = quoteErrorsFrom(err?.error, quotedIdx, QUOTE_FAILED_MESSAGE);
  }

  /**
   * The price on hand answers EXACTLY what the page would ask now: the same
   * walls, sizes, surface and services, and no wall half-typed. Otherwise it is
   * a price for something else — shown only while a newer quote is visibly on
   * its way ("Updating…"), never as the price of what is on screen.
   */
  get quoteIsCurrent(): boolean {
    if (!this.pricingData || !this.quotedSignature || this.hasPartialWall) return false;
    const request = this.quoteRequest();
    return !!request && JSON.stringify(request.params) === this.quotedSignature;
  }

  /** May the price on hand be shown — current, or the last one while a newer loads. */
  get priceShowable(): boolean {
    return this.quoteIsCurrent || this.priceUpdating;
  }

  selfAdhesiveMessage = '';

  selectPaper(paper: any) {
    const prevSurface = this.selectedPaper?.display_name || this.selectedPaper?.name || 'none';
    // T2 — record surface switch (2+ in 2 min → confusion)
    if (this.selectedPaper && this.selectedPaper !== paper) {
      this.triggerService.pdpRecordSurfaceSwitch();
    }
    this.selectedPaper = paper;
    // Keep previous pricingData/lastPrintRate so the displayed "From ₹X/sq.ft."
    // stays stable until the new surface's price returns (~1s API call).
    // Clearing them here caused a flicker to the raw paperPrintRate fallback.
    this.selfAdhesiveMessage = '';
    if (this.isSelfAdhesive) {
      this.selfAdhesiveMessage = 'Installation not available for this Print Surface';
      setTimeout(() => this.selfAdhesiveMessage = '', 5000);
    }
    // C2 — surface_select
    const productId = this.wallpaper?.wallpaper_id || String(this.wallpaper?.id || '');
    this.dataLayer.pushSurfaceSelect({
      productId,
      surfaceType: paper.display_name || paper.name || '',
      previousSurface: prevSurface,
      priceUpdated: null
    });
    // If preview was already generated, track as C10 surface change
    if (this.previewGenerated) {
      this.previewChangeCount++;
      this.dataLayer.pushPreviewChange({
        productId,
        changeType: 'surface',
        oldPrice: this.lastPreviewPrice,
        newPrice: 0, // will update after calculatePrice
        changeCount: this.previewChangeCount
      });
    }
    this.calculatePrice();
    this.savePdpConfig();
  }

  selectSizeType(sType: string) {
    this.selectedSizeType = sType;
    this.savePdpConfig();
    this.pricingData = null;
    this.calculatedPrice = null;
    this.pdpActionCount++;
    // C17 — sample_request when SAMPLE selected
    if (sType === 'SAMPLE') {
      this.dataLayer.pushSampleRequest({
        productId: this.wallpaper?.wallpaper_id || String(this.wallpaper?.id || ''),
        productName: this.wallpaper?.name || '',
        finish: this.selectedPaper?.display_name || this.selectedPaper?.name || 'sample'
      });
    }
    // Auto-calculate for STANDARD, SAMPLE, and CUSTOM (if area exists)
    if (sType === 'STANDARD' || sType === 'SAMPLE') {
      this.calculatePrice();
    } else if (sType === 'CUSTOM' && this.totalWallArea > 0) {
      this.calculatePrice();
    }
  }

  selectSampleOption(option: string) {
    this.selectedSampleOption = option;
    this.pricingData = null;
    this.calculatePrice();
  }

  selectStandardSize(size: any) {
    this.selectedStandardSize = size;
    this.savePdpConfig();
    this.pricingData = null;
    this.calculatedPrice = null;
    this.calculatePrice();
  }


  // ── Multi-wall helpers ─────────────────────────────────────
  addWall() {
    if (!this.canAddWall) {
      return;
    }
    this.walls.push({ width: null, height: null, unit: 'ft', widthFt: undefined, widthIn: undefined, heightFt: undefined, heightIn: undefined });
    // T5 — 3+ walls = high-value VIP
    this.triggerService.pdpAddWall();
  }

  removeWall(index: number) {
    if (this.walls.length > 1) {
      this.walls.splice(index, 1);
      // Per-wall errors are keyed by position: move them with their walls, or a
      // removed wall's neighbour would inherit its message until the next quote.
      this.quoteErrors = withoutWall(this.quoteErrors, index);
      this.onDimensionChange();
    }
  }

  // Recompute decimal feet from ft+in pair inputs (no cap — custom size accepts any dimension)
  updateWallDim(wall: any) {
    const w = (Number(wall.widthFt) || 0) + (Number(wall.widthIn) || 0) / 12;
    const h = (Number(wall.heightFt) || 0) + (Number(wall.heightIn) || 0) / 12;
    wall.width = w;
    wall.height = h;
    wall.unit = 'ft';
    this.onDimensionChange('width', wall.width, 'ft');
    // T1 — user is interacting with size inputs; arm the 20s confusion timer
    this.triggerService.pdpSizeInteracted();
  }

  // On blur: if inches >= 12, carry over to feet automatically
  normalizeInches(wall: any, field: 'width' | 'height') {
    const inKey = field === 'width' ? 'widthIn' : 'heightIn';
    const ftKey = field === 'width' ? 'widthFt' : 'heightFt';
    const totalIn = Number(wall[inKey]) || 0;
    if (totalIn >= 12) {
      wall[ftKey] = (Number(wall[ftKey]) || 0) + Math.floor(totalIn / 12);
      wall[inKey] = totalIn % 12;
      this.updateWallDim(wall);
    }
  }

  onDimensionChange(fieldChanged?: string, newValue?: number, unit?: string) {
    const productId = this.wallpaper?.wallpaper_id || String(this.wallpaper?.id || '');

    // C5 — wall_input_start (first interaction)
    if (!this.wallInputStarted) {
      this.wallInputStarted = true;
      this.dataLayer.pushWallInputStart(productId);
    }

    // C6 — wall_size_change
    if (fieldChanged && newValue !== undefined) {
      this.dataLayer.pushWallSizeChange({
        productId,
        fieldChanged,
        newValue,
        unit: unit || this.walls[0]?.unit || 'ft'
      });
    }

    this.savePdpConfig();
    // The last valid quote STAYS on screen, marked "Updating…", instead of being
    // blanked to "—" on every keystroke. Only when no wall has any area left is
    // there nothing it could still describe — then it goes, as it always did.
    if (this.totalWallArea <= 0) {
      this.pricingData = null;
      this.pricedWallIdx = [];
    }
    // A half-filled wall is not quoted (see hasPartialWall): nothing is pending.
    this.quotePending = !this.hasPartialWall && this.totalWallArea > 0;
    clearTimeout(this.dimensionTimer);
    this.dimensionTimer = setTimeout(() => {
      // T5 — 200+ sq.ft = high-value VIP
      this.triggerService.pdpCheckHighValue({ sqft: this.totalWallArea, wallCount: this.walls.length });
      this.quotePending = false;
      // Every wall must be either empty or have BOTH sizes before a quote goes
      // out — a wall with only its width would otherwise be left out silently.
      if (this.hasPartialWall) {
        return;
      }
      if (this.totalWallArea > 0) {
        // C7 — wall_input_complete
        const wall = this.walls[0];
        this.dataLayer.pushWallInputComplete({
          productId,
          width: wall?.width || 0,
          height: wall?.height || 0,
          unit: wall?.unit || 'ft'
        });
        this.calculatePrice();
      }
    }, PdpComponent.QUOTE_DEBOUNCE_MS);
  }

  // ── Wall completeness (multi-wall UX) ─────────────────────
  // A wall is EMPTY (no size yet), COMPLETE (width and height) or PARTIAL (one of
  // the two). Read from width/height — the feet values updateWallDim() keeps in
  // step with the ft/in inputs, and what a restored draft carries.

  /** Only one of width / height entered. */
  isWallPartial(wall: { width: number | null; height: number | null }): boolean {
    return (Number(wall.width) > 0) !== (Number(wall.height) > 0);
  }

  /** The size still missing on a partial wall, for its hint. */
  missingDimension(wall: { width: number | null; height: number | null }): 'width' | 'height' {
    return Number(wall.width) > 0 ? 'height' : 'width';
  }

  /**
   * 1-based numbers of the walls that are half-filled. Custom sizes only — a
   * standard size is priced from the chosen size, not from these inputs.
   */
  get partialWallNumbers(): number[] {
    if (this.selectedSizeType !== 'CUSTOM') return [];
    return this.walls.map((w, i) => (this.isWallPartial(w) ? i + 1 : 0)).filter(n => n > 0);
  }

  get hasPartialWall(): boolean {
    return this.partialWallNumbers.length > 0;
  }

  /**
   * Which walls the price does NOT cover, said beside it:
   * - a half-typed wall hides the price until it is finished — "Complete wall 3
   *   to see the price";
   * - an empty extra wall is simply not priced — "Wall 3 has no size yet — not
   *   in this price".
   */
  get notIncludedLabel(): string {
    const list = (n: number[]) => (n.length === 1 ? `wall ${n[0]}` : `walls ${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`);
    const partial = this.partialWallNumbers;
    if (partial.length) return `Complete ${list(partial)} to see the price`;
    const empty = this.emptyWallNumbers;
    if (!empty.length || empty.length === this.walls.length) return '';
    const named = list(empty);
    return `${named.charAt(0).toUpperCase()}${named.slice(1)} ${empty.length === 1 ? 'has' : 'have'} no size yet — not in this price`;
  }

  /** 1-based numbers of the walls with no size at all (custom sizes only). */
  get emptyWallNumbers(): number[] {
    if (this.selectedSizeType !== 'CUSTOM') return [];
    return this.walls.map((w, i) => (!(Number(w.width) > 0) && !(Number(w.height) > 0) ? i + 1 : 0)).filter(n => n > 0);
  }

  /** A newer quote is on its way — the price shown is the last valid one. */
  get priceUpdating(): boolean {
    return !!this.pricingData && (this.priceLoading || this.quotePending);
  }

  /**
   * Each wall's line, when the quote priced two or more: its size, its area and
   * its amount (its share of the shared plan when the walls are cut together) —
   * all from the server. Null for a single wall, which keeps the one-line summary
   * it always had.
   */
  get wallLines(): Array<{ label: string; size: string; area: number; amount: number }> | null {
    // Lines priced for other walls are not shown as these walls' lines.
    if (!this.priceShowable) return null;
    const lines = this.quoteSummary?.walls ?? [];
    if (this.selectedSizeType !== 'CUSTOM' || lines.length < 2) return null;
    const sizes: any[] = this.pricingData?.walls ?? [];
    return lines.map((line, i) => ({
      label: `Wall ${(this.pricedWallIdx[i] ?? i) + 1}`,
      size: String(sizes[i]?.size || '').replace(/x/i, ' × ') + (sizes[i]?.size ? ' ft' : ''),
      area: line.wallAreaSqft,
      amount: line.amount,
    }));
  }

  private wallAreaInSqFt(wall: { width: number | null; height: number | null; unit: string }): number {
    let w = wall.width || 0;
    let h = wall.height || 0;
    if (wall.unit === 'in') { w /= 12; h /= 12; }
    if (wall.unit === 'cm') { w *= 3.28 / 100; h *= 3.28 / 100; } // cm to feet via meter
    return w * h;
  }

  get cropPatternImage(): string {
    if (this.variantColors.length > 0) {
      const v = this.variantColors[this.selectedColorIndex]?.data || {};
      return v.pattern || v.highres || v.list_thumbnail || this.wallpaper?.list_thumbnail || '';
    }
    return this.wallpaper?.pattern || this.wallpaper?.highres || this.wallpaper?.hover_thumbnail || this.wallpaper?.list_thumbnail || '';
  }

  get cropAspectRatio(): number {
    const fallback = this.maxCustomDims;
    const w = this.walls[0]?.width || fallback?.wFt || 1;
    const h = this.walls[0]?.height || fallback?.hFt || 1;
    return w / h;
  }

  openCropModal() {
    this.isCropModalOpen = true;
    this.cropperReady = false;
    this.croppedImageBase64 = '';
  }

  closeCropModal() { this.isCropModalOpen = false; }

  onImageCropped(event: ImageCroppedEvent) {
    this.croppedImageBase64 = event.base64 || '';
  }

  get patternPreviewDims(): { w: number; h: number } {
    const fallback = this.maxCustomDims;
    const wFt = this.walls[0]?.width || fallback?.wFt || 1;
    const hFt = this.walls[0]?.height || fallback?.hFt || 1;
    const maxH = 160;
    const maxW = 220;
    let dh = maxH;
    let dw = maxH * (wFt / hFt);
    if (dw > maxW) { dw = maxW; dh = maxW * (hFt / wFt); }
    return { w: Math.round(dw), h: Math.round(dh) };
  }

  get patternPreviewLabel(): { w: string; h: string } {
    const wall = this.walls[0];
    const hasInput = wall && (wall.widthFt || wall.widthIn || wall.heightFt || wall.heightIn);
    if (hasInput) {
      const wIn = Number(wall.widthIn) || 0;
      const hIn = Number(wall.heightIn) || 0;
      return {
        w: `${wall.widthFt || 0}' ${wIn ? wIn + '"' : ''}`.trim(),
        h: `${wall.heightFt || 0}' ${hIn ? hIn + '"' : ''}`.trim()
      };
    }
    // Fallback to standard_size1 when no input yet
    const fallback = this.maxCustomDims;
    if (fallback) {
      const wFtInt = Math.floor(fallback.wFt);
      const wInt = Math.round((fallback.wFt - wFtInt) * 12);
      const hFtInt = Math.floor(fallback.hFt);
      const hInt = Math.round((fallback.hFt - hFtInt) * 12);
      return {
        w: `${wFtInt}' ${wInt ? wInt + '"' : ''}`.trim(),
        h: `${hFtInt}' ${hInt ? hInt + '"' : ''}`.trim()
      };
    }
    return { w: '', h: '' };
  }

  get totalWallArea(): number {
    if (this.selectedSizeType === 'STANDARD') {
      return this.selectedStandardSize.area;
    }
    return parseFloat(this.walls.reduce((sum, wall) => sum + this.wallAreaInSqFt(wall), 0).toFixed(2));
  }

  get totalWallCost(): number {
    if (!this.wallpaper) return 0;
    if (this.pricingData) {
      const d = this.pricingData;
      // amount_payable already includes all rolls from API
      return (d.amount_payable ?? d.total ?? 0) + this.installationFromApi();
    }
    return this.totalWallArea * this.perSqFtDisplay;
  }

  // ── Computed helpers ─────────────────────────────────────────
  get images(): string[] {
    if (!this.wallpaper) {
      return [];
    }
    const imgs: string[] = [];

    // When color variants exist, build image list ONLY from selected variant.
    // No fallback to wallpaper-level fields — those typically mirror the FIRST
    // variant's data, which would leak first-variant images into other variants.
    if (this.variantColors.length > 0) {
      const variant = this.variantColors[this.selectedColorIndex]?.data || {};
      // Space images first (skip space_thumbnail — it's usually the same as list_thumbnail)
      ['space1', 'space2', 'space3', 'space4', 'space5', 'space6'].forEach((k: string) => {
        const url = variant[k];
        if (url && !imgs.includes(url)) imgs.push(url);
      });
      // Flat design / pattern next (variant only)
      const design = variant.pattern || variant.highres || variant.hover_thumbnail;
      if (design && !imgs.includes(design)) imgs.push(design);
      // list_thumbnail as fallback — only if no space images existed
      if (imgs.length === 0) {
        const fb = variant.list_thumbnail || variant.space_thumbnail || variant.image;
        if (fb) imgs.push(fb);
      }
      return imgs;
    }

    // Default: wallpaper-level images — spaces first, design last
    ['space_thumbnail', 'space1', 'space2', 'space3', 'space4', 'space5', 'space6'].forEach(k => {
      const url = this.wallpaper[k];
      if (url && !imgs.includes(url)) {
        imgs.push(url);
      }
    });
    const design = this.wallpaper.pattern || this.wallpaper.highres || this.wallpaper.hover_thumbnail;
    if (design && !imgs.includes(design)) {
      imgs.push(design);
    }
    if (imgs.length === 0) {
      const fb = this.wallpaper.space_thumbnail || this.wallpaper.image;
      if (fb) {
        imgs.push(fb);
      }
    }
    return imgs;
  }

  // get activeImage(): string {
  //   return this.images[this.activeImageIndex] || '';
  // }

  get activeImage(): string {

    if (this.selectedVariantImage) {
      return this.selectedVariantImage;
    }

    return this.images[this.activeImageIndex] || '';
  }

  get imageLabels(): string[] {
    if (!this.wallpaper) return [];
    const labels: string[] = [];
    const fallbackLabels = ['Living Room', 'Bedroom', 'Dining Room', 'Kids Room', 'Offices & Corporate', 'Study'];
    const design = this.wallpaper.pattern || this.wallpaper.highres || this.wallpaper.hover_thumbnail;
    // Spaces first
    let si = 0;
    ['space1', 'space2', 'space3', 'space4', 'space5', 'space6'].forEach(k => {
      const url = this.wallpaper[k];
      if (url && !(design && url === design)) {
        const realName = this.spaceLabels[k];
        labels.push(realName || fallbackLabels[si] || 'Room ' + (si + 1));
        si++;
      }
    });
    // Design last
    if (design) labels.push('Design');
    return labels;
  }

  get wallpaperId(): string {
    return this.wallpaper?.wallpaper_id || this.wallpaper?.sku || '';
  }

  get collectionName(): string {
    return this.wallpaper?.collection_name || this.wallpaper?.collection_slug || '';
  }

  /**
   * Collection type ("premium" / "exclusive"), normalised. The detail API returns
   * a plain string while Solr can send a single-element array, so both are handled.
   */
  private get qualityValue(): string {
    const q = this.wallpaper?.quality;
    if (!q) return '';
    return String(Array.isArray(q) ? q[0] : q).toLowerCase().trim();
  }

  /** Badge text, '' when the product has no recognised collection type. */
  get collectionTypeLabel(): string {
    const q = this.qualityValue;
    if (q === 'exclusive') return 'Exclusive';
    if (q === 'premium') return 'Premium';
    return '';
  }

  /** Same colours as the listing-card ribbon, so the two read as one system. */
  get collectionTypeClass(): string {
    return this.qualityValue === 'exclusive'
      ? 'bg-[#600b0b] text-white'
      : 'bg-[#c49a6c] text-[#4a2f18]';
  }

  get wallpaperType(): string {
    return this.wallpaper?.wallpaper_type || '';
  }

  get description(): string {
    return this.wallpaper?.caption || this.wallpaper?.description || '';
  }

  get rollSize(): string {
    return this.wallpaper?.roll_size || '';
  }

  get detailSpaces(): string {
    if (!this.wallpaper) return '';
    return this.wallpaper.spaces || this.wallpaper.space_names || this.wallpaper.space || '';
  }

  get detailMoods(): string {
    if (!this.wallpaper) return '';
    return this.wallpaper.moods || this.wallpaper.mood || this.wallpaper.theme || this.wallpaper.interior_theme || '';
  }

  get colorNames(): string {
    return this.resolvedColors.map(c => c.name).filter(n => n).join(', ');
  }

  get isWishlisted(): boolean {
    return this.wallpaper ? this.wishlistIds.has(String(this.wallpaper.id)) : false;
  }

  get wallArea(): number {
    const w = parseFloat(this.wallWidth);
    const h = parseFloat(this.wallHeight);
    if (isNaN(w) || isNaN(h)) return 0;
    return parseFloat((w * h).toFixed(2));
  }

  get isSelfAdhesive(): boolean {
    const name = (this.selectedPaper?.display_name || this.selectedPaper?.name || '').toLowerCase();
    return name.includes('self adhesive');
  }

  /**
   * The ₹/sq ft shown on the page — always tax-INCLUSIVE, and always a figure the
   * SERVER computed.
   *
   * What used to be here did its own tax maths and got it wrong three ways:
   *
   *   1. `rate + rate * this.GST` with `GST = 0.18` hardcoded. The rate it was
   *      applied to is ex-tax at the SURFACE's own slab, and those differ — Glitter
   *      is 30%. Every surface except an 18% one displayed 9.2% low (Glitter's real
   *      ₹151 showed as ₹137.06).
   *   2. `paperPrintRate = print_cost × 144` as the first fallback. `print_cost` is
   *      the INK AND MATERIAL COST per square inch — ₹12.64/sq ft for Non-Woven
   *      where the sellable rate is ₹73. The page advertised a cost as a price.
   *   3. `surfacePrice` took `wallpaper.price`, which is already inclusive, and
   *      taxed it again.
   *
   * All three are gone. Three inclusive figures in order of freshness, no
   * arithmetic on any of them:
   *   the live quote → the selected surface's own rate → the design's "from" rate.
   */
  get perSqFtDisplay(): number {
    return this.pricingData?.rate_incl
      ?? this.selectedPaper?.rate
      ?? this.wallpaper?.price
      ?? 0;
  }

  /**
   * The pre-discount rate, for a strikethrough. Same source, and the same rule:
   * inclusive, server-computed, untouched.
   */
  get surfaceOriginalPrice(): number {
    return this.selectedPaper?.rate ?? this.wallpaper?.price ?? this.perSqFtDisplay;
  }

  /* ── Order calculator ──────────────────────────────────────────────────────
   * One getter per line, each reading the SERVER's figure. The template used to
   * mix `pricingData?.amount_payable` with a `calculatedTotalPrice` fallback that
   * returns the string '...', which is why the Wallpaper and Total lines showed
   * "₹…" whenever the quote had not landed — and the quote was 422-ing, so that
   * was always. Installation was worse: it came from `installationCost`, an
   * `area × rate` computed on the client, so it kept showing a number while the
   * rest showed nothing.
   */

  /** Wallpaper, tax-inclusive, after any discount. 0 until the quote lands. */
  /**
   * The price card's figures — paper, wastage, money — exactly as the server
   * quoted them, whether the walls were billed together (cross-wall) or each on
   * its own. See quote-summary.ts.
   */
  get quoteSummary(): PdpQuoteSummary | null {
    if (this.pricingData !== this.summarySource) {
      this.summarySource = this.pricingData;
      this.summaryCache = toQuoteSummary(this.pricingData);
    }
    return this.summaryCache;
  }

  /** The server's message for this wall's last quote, if it refused it. */
  wallQuoteError(wallIndex: number): string | null {
    return this.quoteErrors.byWall[wallIndex] ?? null;
  }

  /** A wall the server refused blocks Add to Cart / Buy Now until it is fixed. */
  get hasWallQuoteErrors(): boolean {
    return Object.keys(this.quoteErrors.byWall).length > 0;
  }

  get calcProductAmount(): number {
    return this.quoteSummary?.wallpaperTotal ?? 0;
  }

  /**
   * Installation as the server resolved it for the pincode, WITH its 18% GST
   * (installation_tax, added on top server-side) — so the row adds up to the
   * quote's 	otal.
   */
  get calcInstallationAmount(): number {
    if (!this.wantInstallation || this.isSelfAdhesive) return 0;
    return (this.pricingData?.installation_amount ?? 0) + (this.pricingData?.installation_tax ?? 0);
  }

  /**
   * Installation as the server charged it — on the walls' AREA (never the billed
   * paper): its area, rate, whether the city's fixed amount applied, the charge
   * and its GST. Null when not chosen or not in the quote.
   */
  get quotedInstallation(): PdpQuoteInstallation | null {
    if (!this.wantInstallation || this.isSelfAdhesive) return null;
    return this.quoteSummary?.installation ?? null;
  }

  /**
   * The installation line's explanation, from the server's figures only: the
   * fixed amount for the walls' area, or rate × area. Falls back to the
   * pincode-check figures for a response that predates them.
   */
  get installationBasisLabel(): string {
    const inst = this.quotedInstallation;
    const area = inst ? inst.sqft : this.installationArea;
    const fixed = inst && inst.fixed !== null ? inst.fixed : this.installationIsFixed;
    const rate = inst && inst.rate !== null ? inst.rate : this.installationRate;
    const sqft = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return fixed
      ? `fixed charge for ${sqft(area)} sq ft wall area`
      : `₹${sqft(Number(rate) || 0)}/sq ft × ${sqft(area)} sq ft wall area`;
  }

  /**
   * Expert-visit fee for the pincode, resolved server-side.
   *
   * It IS part of `total` — a visit-only quote on a ₹10,950 wall comes back
   * 11,950 — so this getter only labels the line; nothing here adds it in. The
   * fee is later credited against the wallpaper bill, but that happens at the
   * order, not in this quote.
   *
   * Falls back to `expertVisitFee` (the same figure off check-pincode) only for
   * the moment between ticking the radio and the re-quote landing.
   */
  get calcExpertVisitAmount(): number {
    if (!this.wantExpertVisit) return 0;
    // The fee plus its service GST, both from the server (business rule, 6 Oct
    // 2026). Before the re-quote lands only the bare fee is known.
    const d = this.pricingData;
    return d?.visit_fee != null ? Number(d.visit_fee) + (Number(d.visit_tax) || 0) : this.expertVisitFee;
  }

  /** Service GST % (installation, measurement) from the server's config. */
  get serviceGstPer(): number {
    return Number(this.pricingData?.service_gst_per) || 0;
  }

  /** What the customer pays for these walls. Straight off the quote. */
  get calcTotal(): number {
    return this.quoteSummary?.grandTotal ?? 0;
  }

  /**
   * Whether the calculator itemises. The condition used to be `wantInstallation`
   * alone, so a visit-only quote fell into the single-line branch and the fee it
   * had just added to the total appeared nowhere — the customer saw the number
   * jump by ₹1,000 with no line explaining it.
   */
  get showServiceBreakdown(): boolean {
    return (this.wantInstallation && !this.isSelfAdhesive) || this.wantExpertVisit;
  }

  /**
   * True once there is a price FOR WHAT IS ON SCREEN (or one is visibly updating);
   * otherwise the block renders a dash. A quote for other walls — a wall since
   * half-typed, sizes since changed — is not a price for these.
   */
  get calcReady(): boolean {
    return !!this.pricingData && this.calcTotal > 0 && this.priceShowable;
  }

  /**
   * Installation and an expert visit are mutually exclusive — the server rejects
   * both together, so the UI must not offer it. Choosing one clears the other,
   * and 'none' clears both.
   *
   * Every path in or out of a site service goes through here, which is the point:
   * turning one OFF has consequences (a booking to undo, a re-quote to fire) and
   * they were previously only wired into the turning-ON path.
   */
  chooseService(service: 'installation' | 'expert' | 'none'): void {
    const wasVisit = this.wantExpertVisit;

    this.wantInstallation = service === 'installation';
    this.wantExpertVisit = service === 'expert';

    // Dropping the visit must drop the consultation line it booked. Selecting the
    // radio calls the cart API, so without this the fee stays in the cart after
    // the customer has taken it off the page — and it fires when switching TO
    // installation too, not only on an explicit deselect.
    if (wasVisit && ! this.wantExpertVisit) {
      this.cancelExpertVisitBooking();
    }

    // The installation terms were accepted for an installation that is no longer
    // selected; re-selecting must ask again.
    if (! this.wantInstallation) {
      this.installationAgreed = false;
    }

    // Re-quote. This is what actually subtracts the charge: `calcTotal` reads the
    // server's `total`, so until a fresh quote lands the old one still carries the
    // service that was just turned off.
    this.calculatePrice();

    // Persist the choice, so back/forward returns to the same selection.
    this.savePdpConfig();
  }

  /**
   * Undo the consultation cart line a visit booked.
   *
   * The booking response carries `cart_ids`, so the row is removed by id rather
   * than by reloading the cart and guessing which line was the consultation.
   * State is cleared before the call, not in its callback: the page must show the
   * fee gone immediately, and a failed delete is a cart the customer can still
   * see and remove themselves — better than a UI that keeps claiming a visit is
   * booked.
   */
  private cancelExpertVisitBooking(): void {
    const cartId = this.expertVisitCartId;
    this.expertVisitBooked = false;
    this.expertVisitCartId = null;

    if (!cartId) return;

    this.state.apiRemoveCartItem(cartId).subscribe({
      next: () => this.state.loadCartFromApi(true),
      error: () => this.state.showToast('Visit removed from the page, but it may still be in your cart.', 'info'),
    });
  }

  /**
   * Installation radio clicked.
   *
   * On `(click)`, not `(change)`, and that is the whole fix: a radio only fires
   * `change` when it becomes checked, so clicking the selected one — the obvious
   * way to say "actually, no installation" — did nothing at all. The charge stayed
   * in the total and the pincode form never came back. `click` fires either way,
   * so the already-selected case can clear it.
   */
  onInstallationClick(input: HTMLInputElement): void {
    if (this.wantInstallation) {
      input.checked = false;
      this.chooseService('none');
      return;
    }

    // The tick must not stick until the terms are accepted. Angular won't rewrite
    // the DOM on its own here — the bound value stays false, so the binding sees
    // no change while the browser has already checked the box. Clear it by hand;
    // accepting sets wantInstallation, which then ticks it through the binding.
    input.checked = false;
    this.openInstallationModal();
  }

  /** Expert-visit radio clicked. Same deselect rule as installation. */
  onExpertVisitClick(input: HTMLInputElement): void {
    if (this.wantExpertVisit) {
      input.checked = false;
      this.chooseService('none');
      return;
    }

    this.chooseService('expert');
    if (!this.expertVisitBooked) {
      this.bookExpertVisit();
    }
  }

  /**
   * Installation from the price API, gated on the customer's opt-in.
   *
   * pricingData can still carry an amount from an earlier call made while the box
   * was ticked, so without this gate unticking would leave a stale charge in the
   * displayed totals.
   */
  private installationFromApi(): number {
    if (!this.wantInstallation || this.isSelfAdhesive) return 0;
    const d = this.pricingData;
    return d ? (d.installation_amount || 0) + (d.installation_tax || 0) : 0;
  }

  /** Area the installation is charged on — every wall of the order, together. */
  get installationArea(): number {
    return this.selectedSizeType === 'STANDARD'
      ? this.selectedStandardSize.area
      : this.totalWallArea > 0 ? this.totalWallArea : this.wallArea;
  }

  /**
   * True when a flat amount is billed instead of area × rate — inside the fixed
   * band, or (no band configured) floored at the city minimum. Mirrors
   * InstallationCity::isFixedCharge on the server.
   */
  get installationIsFixed(): boolean {
    const area = this.installationArea;
    const fixed = this.installationFixedAmount;
    if (fixed == null || fixed <= 0 || area <= 0) return false;
    return this.installationUptoSqft != null
      ? area <= this.installationUptoSqft
      : fixed > area * this.installationRate;
  }

  get installationCost(): number {
    // When API data is present, installation is already in pricingData
    // This getter is only used for local fallback display. Same rule as the
    // server (InstallationCity::chargeFor), on ALL walls' area, once.
    if (!this.pincodeAvailable) return 0;
    if (this.installationIsFixed) return this.installationFixedAmount as number;
    return this.installationArea * this.installationRate;
  }

  /** Take the fixed-charge band off a pincode-check response. */
  private applyInstallationBand(res: any): void {
    const amount = Number(res?.min_charge);
    const upto = Number(res?.flat_upto_sqft);
    this.installationFixedAmount = Number.isFinite(amount) && amount > 0 ? amount : null;
    this.installationUptoSqft = Number.isFinite(upto) && upto > 0 ? upto : null;
  }

  get discountRate(): number {
    // The backend is the source of truth; wallpapers now carry no flat discount,
    // so the fallback is 0 rather than an assumed 15%.
    if (this.pricingData?.discount_rate != null) return this.pricingData.discount_rate;
    return 0;
  }

  get mrpTotal(): string {
    if (this.selectedSizeType === 'SAMPLE') {
      // Sample MRP from API
      if (this.pricingData) {
        const d = this.pricingData;
        const payable = d.amount_payable ?? d.total ?? 0;
        const discount = d.discount_with_tax ?? 0;
        return Math.round(payable + discount).toFixed(0);
      }
      return '...';
    }
    if (this.pricingData) {
      const d = this.pricingData;
      // MRP = amount_payable + discount (before discount was applied) + installation
      const payable = d.amount_payable ?? d.total ?? 0;
      const discount = d.discount_with_tax ?? 0;
      // `discount_with_tax` above already carries the inclusive discount, and the
        // server sends it — so a second, client-taxed discount term would double
        // count. Kept as the raw ex-tax figure it is.
        const printDiscount = d.printCostDiscount ?? 0;
      const install = this.installationFromApi();
      return Math.round(payable + discount + printDiscount + install).toFixed(0);
    }
    return '...';
  }

  get calculatedTotalPrice(): string {
    if (this.selectedSizeType === 'SAMPLE') {
      // Sample price comes from API
      if (this.pricingData) {
        const d = this.pricingData;
        return this.formatPrice(d.amount_payable ?? d.total ?? d.price ?? 0);
      }
      return '...'; // loading
    }

    // API price available — finalPrice = amount_payable + installation
    if (this.pricingData) {
      const d = this.pricingData;
      const payable = d.amount_payable ?? d.total ?? d.price ?? 0;
      // Prefer API's installation amount; fallback to local installationCost
      // (covers the case where the price call was made before the customer opted
      // in, so the API total carries no installation yet)
      let install = this.installationFromApi();
      if (!install && this.wantInstallation && !this.isSelfAdhesive) {
        install = this.installationCost;
      }
      return this.formatPrice(payable + install);
    }

    // No API data yet — show loading indicator
    return '...';
  }

  // Show 2 decimals when there are any; whole number when value is integer.
  private formatPrice(n: number): string {
    const rounded = Math.round(n * 100) / 100;
    return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(2);
  }

  /**
   * Editing the pincode invalidates the previous lookup. Without this the green
   * "available" tick would stay on screen for a pincode that was never verified,
   * and opting in would send that unverified pincode to the price/cart payload.
   * The opt-in is cleared too — serviceability was confirmed for a different area.
   */
  onPincodeChanged(value: string) {
    this.pincode = value;
    if (!this.pincodeAvailable && !this.pincodeMessage && !this.wantInstallation) return;

    const wasSelected = this.wantInstallation;
    this.pincodeAvailable = false;
    this.pincodeMessage = '';
    this.installationAgreed = false;

    // Editing the pincode after opting in used to set `wantInstallation = false`
    // and stop there, so the charge for the OLD pincode stayed in the total until
    // something else happened to re-quote. Clearing through chooseService drops it
    // for real.
    if (wasSelected) {
      this.chooseService('none');
    } else {
      this.wantInstallation = false;
    }
  }

  checkPincode() {
    if (!this.pincode || this.pincode.length < 6) {
      this.pincodeMessage = 'Please enter a valid 6-digit pincode';
      this.pincodeAvailable = false;
      return;
    }

    // Self Adhesive → no installation needed
    if (this.isSelfAdhesive) {
      this.pincodeMessage = 'Self Adhesive wallpapers do not require installation';
      this.pincodeAvailable = false;
      return;
    }

    this.pincodeChecking = true;
    this.pincodeMessage = '';
    // Drop the previous result before re-checking, otherwise the old green tick
    // stays on screen while a different pincode is being verified.
    this.pincodeAvailable = false;
    this.wallpaperService.checkInstallationPincode(this.pincode).subscribe({
      next: (res: any) => {
        this.pincodeChecking = false;
        const productId = this.wallpaper?.wallpaper_id || String(this.wallpaper?.id || '');
        // `serviceable` — the new endpoint's field, replacing the old `{ exist: bool }`.
          // Unlike that flag it arrives with the city, the ₹/sq ft, the minimum
          // charge and the expert-visit fee, so the page can show the rate the
          // customer will actually be charged instead of only "yes, we cover you".
          // `null` means the postal lookup was unreachable — not-yet-known rather
          // than not-serviceable, which is why this tests for `true` explicitly.
          if (res?.serviceable === true) {
          this.pincodeAvailable = true;
          // The rates for THIS city, straight from Production's panels. Nothing is
          // assumed: an unset rate stays 0 and the page shows no figure rather than
          // a default no city actually charges.
          this.installationRate = res.rate_per_sqft ?? 0;
          this.applyInstallationBand(res);
          this.expertVisitFee = res.visit_charge ?? 0;
          this.pincodeMessage = res.city
              ? `Installation available in ${res.city}`
              : 'Installation available in your area!';
          this.dataLayer.pushDeliveryCheck({ productId, pincode: this.pincode, result: 'serviceable', estimatedDays: res.estimated_days || null });
          // Serviceability only — the agreement belongs to the moment the customer
          // actually opts in, i.e. ticks "Include installation in my order".
        } else {
          this.pincodeAvailable = false;
          this.pincodeMessage = 'Installation not available in your area';
          // T6 — pincode unserviceable, fire immediately
          this.triggerService.pdpPincodeNotServiceable();
          this.dataLayer.pushDeliveryCheck({ productId, pincode: this.pincode, result: 'not_serviceable', estimatedDays: null });
        }
      },
      error: () => {
        this.pincodeChecking = false;
        this.pincodeAvailable = false;
        this.pincodeMessage = 'Unable to verify pincode. Please try again.';
      }
    });
  }

  // ── Actions ──────────────────────────────────────────────────
  toggleWishlist() {
    if (!this.wallpaper) return;
    const wp = this.wallpaper;
    const isAdding = !this.wishlistIds.has(String(wp.id));
    this.state.toggleWishlist(String(wp.id), {
      id: String(wp.id),
      name: wp.name,
      image: wp.list_thumbnail || (wp as any).image || wp.hover_thumbnail || wp.space1 || '',
      price: wp.price,
      wallpaper_type: wp.wallpaper_type,
      wallpaper_id: wp.wallpaper_id,
      slug: wp.slug
    });
    // C12 — add_to_wishlist from PDP
    if (isAdding) {
      this.dataLayer.pushAddToWishlistPdp({
        item: wp,
        variant: this.selectedPaper?.display_name || this.selectedPaper?.name || '',
        category: wp.category_name || wp.collection_name || wp.collection_slug || this.context.selected_category || ''
      });
    }
    this.pdpActionCount++;
  }

  isItemWishlisted(id: string): boolean { return this.wishlistIds.has(id); }

  toggleWishlistItem(wp: any, e: Event) {
    e.stopPropagation();
    const id = wp.id.toString();
    const isAdding = !this.wishlistIds.has(id);
    this.state.toggleWishlist(id, {
      id,
      name: wp.name,
      image: wp.list_thumbnail,
      price: wp.price,
      wallpaper_type: wp.wallpaper_type,
      wallpaper_id: wp.wallpaper_id,
      collection_slug: wp.collection_slug,
      slug: wp.slug
    });
    if (isAdding) {
      this.dataLayer.pushAddToWishlist({
        item: wp, source: 'pdp-similar',
        categoryFallback: this.wallpaper?.category_name || ''
      });
    }
  }

  // buildCustomizedProduct() was here: ~90 lines that assembled nested
  // customizedProductItems, a wallpaperSizeArr JSON string and a client-computed
  // pricing_detail for the old cart endpoint. That endpoint discarded both JSON
  // blobs and recomputed the price from a hardcoded matrix, so the arithmetic was
  // decoration over a number the server overwrote. Deleted rather than left dead:
  // buildSiteCartPayload() is the contract now, and money is the server's job.

  // Also keep local cart item for display
  private buildLocalCartItem(): any {
    const wp = this.wallpaper;
    const sizeLabel = this.selectedSizeType === 'STANDARD'
      ? this.selectedStandardSize.label
      : this.selectedSizeType === 'SAMPLE'
        ? '8" x 11" (Sample)'
        : this.walls.map((w: any) => `${w.width}x${w.height} ${w.unit}`).join(', ');
    return {
      ...wp,
      calculated_total: parseFloat(this.calculatedTotalPrice) || 0,
      per_sqft_price: this.perSqFtDisplay,
      per_sqft_mrp: this.surfaceOriginalPrice,
      total_area: this.totalWallArea,
      discount_rate: this.discountRate,
      no_of_rolls: this.noOfRolls,
      paper_id: this.selectedPaper?.id || null,
      surface_name: this.selectedPaper?.display_name || this.selectedPaper?.name || '',
      size_type: this.selectedSizeType === 'STANDARD' ? 1 : this.selectedSizeType === 'SAMPLE' ? 3 : 2,
      size_type_label: this.selectedSizeType,
      size_label: sizeLabel,
      pricing_data: this.pricingData ? { ...this.pricingData } : null,
    };
  }

  addingToCart = false;
  buyingNow = false;

  /**
   * The add-to-cart payload for the NEW backend.
   *
   * Replaces the old buildCustomizedProduct() (now deleted), which was 90
   * lines of nested `customizedProductItems`, a `wallpaperSizeArr` JSON string, and
   * a `pricing_detail` the browser computed. The old endpoint dropped both JSON
   * blobs on the floor and recomputed the price from a hardcoded matrix, so all that
   * arithmetic was decoration over a number the server would overwrite.
   *
   * Here the client sends only what it KNOWS — which design, which surface, which
   * walls, which services — and the server prices it with the same engine that
   * quoted the page. Nothing about money crosses the wire.
   *
   * `pincode` is sent only when a service actually needs it. An empty pincode on a
   * serviceless line is what left `installation_pincode` populated with no charge
   * behind it (cart 403 on dev2), which every reader keying off that column then
   * read as "installation wanted".
   */
  private buildSiteCartPayload(): {
    wallpaper_id: number; paper_id: number; quantity: number;
    walls: Array<{ width: number; height: number }>;
    installation: boolean; expert_visit: boolean; pincode?: string;
  } {
    const wantsInstall = this.wantInstallation && !this.isSelfAdhesive && this.pincodeAvailable;
    const wantsVisit = this.wantExpertVisit;
    const pincode = wantsVisit ? (this.expertPincode || this.pincode) : this.pincode;

    return {
      wallpaper_id: Number(this.wallpaper?.id),
      paper_id: Number(this.selectedPaper?.id),
      quantity: this.noOfRolls || 1,
      // One entry per wall — the server writes one cart row each, the way the old
      // backend did by replicating the customized_product.
      walls: this.siteCartWalls(),
      installation: wantsInstall,
      expert_visit: wantsVisit,
      ...((wantsInstall || wantsVisit) && pincode ? { pincode } : {}),
    };
  }

  /**
   * Clear everything about the SITE SERVICES for this page.
   *
   * Called when a different design is opened and again after a line is added to the
   * cart. Both were leaking: `loadWallpaper` reset the walls and the surface but not
   * the service selection, and add-to-cart cleared the stored draft while leaving the
   * component exactly as it was. So the next configuration started with installation
   * already ticked, a pincode from the previous product, and — worse —
   * `expertVisitCartId` still pointing at a consultation row that is now sitting in
   * the cart, which the next deselect would have deleted.
   *
   * The rates go with the flags on purpose: they belong to a pincode that is no
   * longer verified, and keeping them would let the page quote ₹21/sq ft for a city
   * it has not checked.
   */
  private resetServiceSelection(): void {
    this.wantInstallation = false;
    this.wantExpertVisit = false;
    this.installationAgreed = false;
    this.expertVisitBooked = false;
    this.expertVisitCartId = null;

    this.pincode = '';
    this.pincodeAvailable = false;
    this.pincodeMessage = '';
    this.pincodeChecking = false;

    this.expertPincode = '';
    this.expertPincodeAvailable = false;
    this.expertPincodeMessage = '';
    this.expertPincodeChecking = false;

    this.installationRate = 0;
    this.installationFixedAmount = null;
    this.installationUptoSqft = null;
    // The global "from" fee survives — it is not tied to a pincode, and the
    // accordion quotes it before any check. A city's own fee is re-resolved.
    this.expertVisitFee = Number(this.visitTerms?.fee_from) || 0;

    this.showInstallDetail = false;
    this.showExpertDetail = false;
  }

  /** The walls, in the shape the cart endpoint takes. */
  private siteCartWalls(): Array<{ width: number; height: number }> {
    if (this.selectedSizeType === 'STANDARD') {
      return [{ width: this.selectedStandardSize.w, height: this.selectedStandardSize.h }];
    }
    if (this.selectedSizeType === 'SAMPLE') {
      return [{ width: this.SAMPLE_WIDTH, height: this.SAMPLE_HEIGHT }];
    }
    // 4 decimals, not 2: 10ft 5in is 10.4167, and rounding it to 10.42 inflates the
    // area by ~0.07 sq ft — enough to make the cart disagree with the PDP.
    // Only MEASURED walls — the same ones the price above was quoted on. A wall
    // half-filled (width, no height) used to go to the cart as a 1-ft-high line the
    // quote never included, and its area joined the pooled installation.
    return this.walls
      .filter(w => (Number(w.width) || 0) > 0 && (Number(w.height) || 0) > 0)
      .map(w => ({
        width: parseFloat(Number(w.width).toFixed(4)),
        height: parseFloat(Number(w.height).toFixed(4)),
      }));
  }

  addToCart() {
    if (!this.wallpaper) return;
    // A Measurement Service on its own needs no wall size (it measures the wall).
    if (this.visitOnly) { this.orderVisitOnly(false); return; }
    const error = this.validateBeforeCart();
    if (error) { this.state.showToast(error, 'info'); return; }

    // Mark cart so T8 (homepage return-visitor trigger) doesn't fire after purchase intent
    this.triggerService.markCartAdded();
    this.addingToCart = true;

    this.addToCartAttempted = true;
    this.state.apiAddWallpaperToCart(this.buildSiteCartPayload()).subscribe({
      next: () => {
        this.addingToCart = false;
        this.state.showToast('Added to cart!', 'cart');
        this.state.loadCartFromApi(true);
        // The draft has done its job — the configuration now lives on the cart row.
        // Left behind, it would repopulate the page on a later visit with a wall the
        // customer has already ordered. `clearPdpConfig` existed for this and was
        // never called.
        this.clearPdpConfig();
        // And the component itself, or a second add from this same page starts with
        // the first line's services still selected. That was the visible bug: the
        // pincode form would not open on the next configuration because installation
        // was still ticked from the previous one.
        this.resetServiceSelection();
        this.pricingData = null;
        this.priceLoading = false;
        // The size goes back to one empty wall as well. That line is in the cart now,
        // so leaving 9 × 4 on screen invites the customer to add the same wall twice
        // without noticing — and it is what made the page look like it had
        // "remembered" the previous order.
        this.walls = [{ width: null, height: null, unit: 'ft', widthFt: undefined, widthIn: undefined, heightFt: undefined, heightIn: undefined }];
        this.noOfRolls = 0;
        // C13 — add_to_cart: fire from frontend (GTM → GA4/Ads) AND server-side for Meta CAPI
        const wp = this.wallpaper;
        this.dataLayer.pushAddToCartPdp({
          item: wp,
          variant: this.selectedPaper?.display_name || this.selectedPaper?.name || '',
          category: wp.category_name || wp.collection_name || wp.collection_slug || this.context.selected_category || '',
          quantity: this.noOfRolls || 1,
          price: this.pricingData?.amount_payable || wp.price || 0
        });
      },
      error: (err: any) => {
        this.addingToCart = false;
        const msg = err?.error?.message || err?.message || 'Failed to add to cart. Please try again.';
        this.state.showToast(msg, 'info');
      }
    });
  }

  buyNow() {
    if (!this.wallpaper) return;
    // A Measurement Service on its own needs no wall size (it measures the wall).
    if (this.visitOnly) { this.orderVisitOnly(true); return; }
    const error = this.validateBeforeCart();
    if (error) { this.state.showToast(error, 'info'); return; }

    this.buyingNow = true;

    // Same endpoint and same payload as Add to Cart — Buy Now differs only in where
    // it sends the customer afterwards, so it must not differ in how the line is
    // priced. It was still on the old backend, which would have written this line
    // from the hardcoded matrix while Add to Cart wrote it from the paper chain.
    this.state.apiAddWallpaperToCart(this.buildSiteCartPayload()).subscribe({
      next: (res: any) => {
        this.buyingNow = false;
        // The configuration now lives on the cart row(s), so the PDP draft has done
        // its job. Clear it — exactly as addToCart() does at its success — otherwise
        // an abandoned Buy Now leaves `pdp_draft_<id>` behind and reopening this
        // wallpaper (or another of its surfaces) repopulates the walls / installation
        // the customer has already sent to the cart. This is the only asymmetry that
        // made Buy Now leak where Add to Cart did not. Cart persistence is untouched:
        // the rows are on the backend and in cart$; only this browser draft is dropped.
        this.clearPdpConfig();
        this.resetServiceSelection();
        // Backend returns every cart row it created (a multi-wall product makes one row per
        // wall) in `cart_ids`. Deterministic — no cart reload/diff/snapshot needed.
        const ids: number[] = (res?.cart_ids && res.cart_ids.length)
          ? res.cart_ids
          : (res?.data?.id ? [res.data.id] : []);
        if (ids.length === 0) {
          // Item was added but we couldn't identify its row — don't strand the user on an
          // empty Buy Now page; send them to the normal cart instead.
          this.state.showToast('Added to cart. Please review and checkout.', 'cart');
          this.state.cartLoaded$.next(false);
          this.router.navigate(['/checkout']);
          return;
        }
        this.goToBuyNow(ids);
      },
      error: (err: any) => {
        this.buyingNow = false;
        const msg = err?.error?.message || err?.message || 'Failed to add to cart. Please try again.';
        this.state.showToast(msg, 'info');
      }
    });
  }

  /** Send the user to the single-product Buy Now checkout for the given cart row id(s). */
  private goToBuyNow(ids: number[]) {
    // Ensure the Buy Now page fetches a fresh cart so the new row(s) are present.
    this.state.cartLoaded$.next(false);
    this.router.navigate(['/buy-now'], { queryParams: { items: ids.join(',') } });
  }

  checkPincodeAndBook() {
    if (!this.expertPincode || this.expertPincode.length < 6) {
      this.expertPincodeMessage = 'Please enter a valid 6-digit pincode';
      return;
    }
    this.expertPincodeChecking = true;
    this.expertPincodeMessage = '';
    this.wallpaperService.checkInstallationPincode(this.expertPincode).subscribe({
      next: (res: any) => {
        this.expertPincodeChecking = false;
        // An expert visit has its OWN availability: a city can be serviceable for
        // installation and still have no measurement fee set, so this checks
        // `visit_charge` rather than assuming one implies the other. The old
        // `{ exist: bool }` could not express that difference at all.
        if (res?.serviceable === true && res?.visit_charge) {
          this.expertPincodeMessage = '';
          this.expertPincodeAvailable = true;
          // The fee for THIS city. It was a literal ₹999 in the template, which
          // meant Production could change it in the Measurement Service panel and
          // the site would keep quoting the old number.
          this.expertVisitFee = res.visit_charge;
          if (res.rate_per_sqft) {
            this.installationRate = res.rate_per_sqft;
            this.applyInstallationBand(res);
          }

          // A serviceability check is NOT an opt-in — the customer still has to
          // tick the radio. If a visit was already selected, this is them moving
          // to a different pincode, so the old selection goes (and with it the
          // line it booked, via chooseService) rather than leaving a booking
          // against a pincode they have left behind. Cleared through chooseService
          // and not by assigning the flag, so the total re-quotes; assigning it
          // was why the fee stayed on screen.
          if (this.wantExpertVisit) {
            this.chooseService('none');
          }
        } else {
          this.expertPincodeMessage = 'Expert visit not available at this pincode';
          this.expertPincodeAvailable = false;
          // T6 — pincode unserviceable, fire immediately
          this.triggerService.pdpPincodeNotServiceable();
        }
      },
      error: () => {
        this.expertPincodeChecking = false;
        this.expertPincodeMessage = 'Unable to verify pincode. Please try again.';
      }
    });
  }

  /**
   * Book a standalone expert visit on the NEW backend.
   *
   * This replaces 60 lines that hand-built a fake `customizedProduct` — product_type
   * 24, a `customizedProductItems` array of zeroed frame/mount/glass fields, a
   * caption assembled from the terms, and `price: 999` — only for the old backend to
   * ignore that price and charge it through `SamplePriceObject`, which treats 999 as
   * EX-tax and grosses it up to ₹1,178.82. The city's own fee is ₹1,000.
   *
   * Now the client sends the one thing it knows: the pincode, plus the design as
   * context. The server resolves the fee from `installation_cities` — the same lookup
   * the PDP quoted from, so the cart cannot disagree with the page.
   *
   * Kept as its own cart row, not folded into a wallpaper line, because a visit
   * exists to measure a wall the customer has NOT ordered yet: there may be no
   * wallpaper line to attach it to.
   */
  bookExpertVisit(onBooked?: () => void) {
    if (this.bookingConsultation) return;
    const pincode = this.expertPincode || this.pincode;
    if (!pincode || pincode.length < 6) {
      this.expertPincodeMessage = 'Please enter a valid 6-digit pincode';
      return;
    }
    this.bookingConsultation = true;

    this.state.apiBookExpertVisit(pincode, Number(this.wallpaper?.id) || undefined).subscribe({
      next: (res: any) => {
        this.bookingConsultation = false;
        this.expertVisitBooked = true;
        this.expertVisitCartId = Number(res?.cart_id) || null;
        // The fee the SERVER charged, echoed back, so the page stops showing an
        // estimate the moment the real figure is known.
        if (res?.visit_fee) this.expertVisitFee = Number(res.visit_fee);
        this.state.showToast('Expert visit booked! Added to cart.', 'cart');
        this.state.loadCartFromApi(true);
        onBooked?.();
      },
      error: (err: any) => {
        this.bookingConsultation = false;
        // A refusal here is a real answer — the city may have no visit fee set — so
        // it is shown rather than swallowed, and the selection is dropped so the UI
        // does not claim a booking that does not exist.
        const msg = err?.error?.message
          || err?.error?.errors?.pincode?.[0]
          || 'Could not book the visit. Please try again.';
        this.expertPincodeMessage = msg;
        this.chooseService('none');
      }
    });
  }

  /**
   * MEASUREMENT SERVICE ON ITS OWN: the visit is chosen and no wall size is
   * entered (custom size, area 0). The expert comes to measure, so there is no
   * wallpaper to size or price yet — the wall-size check does not apply. The
   * visit's own cart line was booked the moment it was selected.
   */
  get visitOnly(): boolean {
    return this.wantExpertVisit && this.selectedSizeType === 'CUSTOM' && this.totalWallArea <= 0;
  }

  /**
   * Add to Cart / Buy Now for a visit on its own: no wallpaper line is written.
   * The visit line (booked on selection) is the purchase — Buy Now opens
   * checkout for exactly that line; Add to Cart confirms it is in the cart.
   */
  private orderVisitOnly(buyNow: boolean): void {
    const finish = (justBooked: boolean) => {
      const id = this.expertVisitCartId;
      // The visit now lives on its cart row; the page starts over for the next
      // item. resetServiceSelection clears the flags WITHOUT deleting that row.
      this.clearPdpConfig();
      this.resetServiceSelection();
      if (buyNow) {
        if (id) {
          this.goToBuyNow([id]);
        } else {
          this.state.cartLoaded$.next(false);
          this.router.navigate(['/checkout']);
        }
        return;
      }
      if (!justBooked) this.state.showToast('Measurement Service is in your cart.', 'cart');
    };
    if (this.expertVisitBooked) {
      finish(false);
      return;
    }
    // Not booked yet (still in flight, or it failed and was reselected) — book now.
    this.bookExpertVisit(() => finish(true));
  }

  private validateBeforeCart(): string | null {
    // 1. Custom wallpaper must have an uploaded image
    if (this.wallpaper?.wallpaper_type === 'custom' && !this.wallpaper?.image) {
      return 'Please upload an image for custom wallpaper!';
    }

    // 2. Surface type must be selected
    if (!this.selectedPaper && this.selectedSizeType !== 'SAMPLE') {
      return 'Please select a surface type.';
    }

    // 2b. A wall the server could not price cannot go into the cart.
    const refusedWall = Object.keys(this.quoteErrors.byWall).map(Number).sort((a, b) => a - b)[0];
    if (refusedWall !== undefined) {
      return `Wall ${refusedWall + 1}: ${this.quoteErrors.byWall[refusedWall]}`;
    }

    // 3. Minimum order size (non-sample only) — on ALL the walls together, since
    // the walls are cut and billed as one job: a narrow wall beside others is not
    // a small order. The minimum is the server's (minOrderSqft).
    if (this.selectedSizeType === 'CUSTOM') {
      if (this.totalWallArea <= 0) {
        return 'Please enter wall dimensions.';
      }
      if (this.minOrderSqft > 0 && this.totalWallArea < this.minOrderSqft) {
        return `Minimum order is ${this.minOrderSqft} sq.ft. (all walls together).`;
      }
    }

    // 4. Standard size must be selected
    if (this.selectedSizeType === 'STANDARD' && !this.selectedStandardSize) {
      return 'Please select a standard size.';
    }

    // 5. Price must be > 0 (API failure check)
    if (this.selectedSizeType !== 'SAMPLE') {
      const price = this.perSqFtDisplay;
      if (!price || price <= 0) {
        return 'Price calculation failed. Please try again.';
      }
    }

    // 6. Pricing data must be loaded (ensures backend gets valid pricing_detail)
    if (!this.pricingData && this.selectedSizeType !== 'SAMPLE') {
      // Try to calculate price first
      this.calculatePrice();
      return 'Please wait — calculating price. Try again in a moment.';
    }

    return null; // All validations passed
  }


  // ── PDP Draft (localStorage persistence) ─────────────────────
  private pdpDraftKey(): string {
    return AppStateService.PDP_DRAFT_PREFIX + (this.wallpaper?.id || '');
  }

  /**
   * Persist the customer's CHOICES. Never the money.
   *
   * `pricingData` and `noOfRolls` used to be saved here, and that was a stale-price
   * vector: a draft written before a rate change was restored on the next visit and
   * the page showed the old total until something happened to re-quote. Prices are
   * resolved server-side on every load now — the draft only has to remember what
   * the customer picked so the server can be asked the same question again.
   *
   * The service rates (`installationRate`, `expertVisitFee`) are left out for the
   * same reason: the CHOICE is restored, then the pincode is re-verified so the
   * ₹/sq ft and visit fee come from Production's panels rather than from storage.
   */
  private savePdpConfig(): void {
    if (!this.wallpaper?.id) return;
    try {
      localStorage.setItem(this.pdpDraftKey(), JSON.stringify({
        walls: this.walls,
        selectedPaperId: this.selectedPaper?.id ?? null,
        selectedSizeType: this.selectedSizeType,
        selectedStandardSizeLabel: this.selectedStandardSize?.label ?? null,
        selectedColorIndex: this.selectedColorIndex,
        quantity: this.quantity,
        // Site services. These were not saved at all, which is why going back and
        // forward lost the installation / expert-visit choice along with the
        // pincode behind it.
        pincode: this.pincode,
        expertPincode: this.expertPincode,
        wantInstallation: this.wantInstallation,
        wantExpertVisit: this.wantExpertVisit,
        installationAgreed: this.installationAgreed,
        // A booked visit is a real cart row; forgetting its id would orphan it, so
        // returning to the page can still remove it.
        expertVisitBooked: this.expertVisitBooked,
        expertVisitCartId: this.expertVisitCartId,
      }));
    } catch {}
  }

  private restorePdpConfig(): void {
    if (!this.wallpaper?.id) return;
    try {
      const raw = localStorage.getItem(this.pdpDraftKey());
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (Array.isArray(draft.walls) && draft.walls.length) this.walls = draft.walls;
      if (draft.selectedSizeType) this.selectedSizeType = draft.selectedSizeType;
      if (draft.selectedColorIndex != null) this.selectedColorIndex = draft.selectedColorIndex;
      if (draft.pincode) this.pincode = draft.pincode;
      if (draft.selectedStandardSizeLabel) {
        const match = this.standardSizes.find(s => s.label === draft.selectedStandardSizeLabel);
        if (match) this.selectedStandardSize = match;
      }
      if (draft.quantity != null) this.quantity = draft.quantity;

      // Site services: the choice comes back, the rates do not. `rehydrateServices()`
      // re-verifies the pincode so the ₹/sq ft and the visit fee are read from
      // Production's panels, then re-quotes. Until that lands the calculator shows a
      // dash rather than a figure from storage.
      if (draft.expertPincode) this.expertPincode = draft.expertPincode;
      this.wantInstallation = !!draft.wantInstallation;
      this.wantExpertVisit = !!draft.wantExpertVisit;
      this.installationAgreed = !!draft.installationAgreed;
      this.expertVisitBooked = !!draft.expertVisitBooked;
      this.expertVisitCartId = draft.expertVisitCartId ?? null;

      // No cached price is restored any more, so anything with a size to price is
      // loading until the quote answers — that is what stops a "₹…" flash.
      if (this.totalWallArea > 0 || draft.selectedSizeType === 'STANDARD') {
        this.priceLoading = true;
      }
    } catch {}
  }

  /**
   * Re-establish the restored site-service choice against the server.
   *
   * A draft can only say "the customer wanted installation at 201003". It cannot
   * say what installation costs there — that is a rate ops edits in the panel — so
   * the pincode is checked again and the fresh figures drive the quote. Called once
   * the surfaces have loaded, because a quote needs a paper.
   *
   * Silent by design: this is a restore, not the customer asking. If the pincode is
   * no longer serviceable the choice is dropped rather than carried on a rate that
   * no longer exists.
   */
  private rehydrateServices(): void {
    if (!this.wantInstallation && !this.wantExpertVisit) return;

    const pincode = this.wantExpertVisit ? (this.expertPincode || this.pincode) : this.pincode;
    if (!pincode || pincode.length < 6) { this.chooseService('none'); return; }

    this.wallpaperService.checkInstallationPincode(pincode).subscribe({
      next: (res: any) => {
        if (res?.serviceable !== true) { this.chooseService('none'); return; }

        this.installationRate = res.rate_per_sqft ?? 0;
        this.applyInstallationBand(res);
        this.expertVisitFee = res.visit_charge ?? this.expertVisitFee;

        if (this.wantExpertVisit) {
          // A visit needs a fee at this pincode; installation needs a ₹/sq ft.
          if (!res.visit_charge) { this.chooseService('none'); return; }
          this.expertPincodeAvailable = true;
        } else {
          if (!res.rate_per_sqft) { this.chooseService('none'); return; }
          this.pincodeAvailable = true;
          this.pincodeMessage = res.city ? `Installation available in ${res.city}` : 'Installation available in your area!';
        }
        this.calculatePrice();
      },
      // Unreachable lookup is not "not serviceable" — leave the choice alone and
      // let the quote run without it rather than silently dropping the customer's
      // selection on a network blip.
      error: () => this.calculatePrice(),
    });
  }

  private restorePaperFromDraft(): void {
    if (!this.wallpaper?.id || !this.papers.length) return;
    try {
      const raw = localStorage.getItem(this.pdpDraftKey());
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft.selectedPaperId != null) {
        const paper = this.papers.find((p: any) => p.id === draft.selectedPaperId);
        if (paper) this.selectedPaper = paper;
      }
    } catch {}
  }

  private clearPdpConfig(): void {
    if (!this.wallpaper?.id) return;
    try { localStorage.removeItem(this.pdpDraftKey()); } catch {}
  }

  navigateToPDP(wp: SearchWallpaper) {
    const sid = String(wp.id);
    this.state.updateContext({ interaction_history: [...this.context.interaction_history.filter(x => x !== sid), sid] });
    // C14b — recommendation_click
    const idx = this.similarWallpapers.indexOf(wp) + 1;
    this.dataLayer.pushRecommendationClick({
      type: 'similar',
      item: wp,
      index: idx,
      sourceProductId: this.wallpaper?.wallpaper_id || String(this.wallpaper?.id || '')
    });
    this.router.navigateByUrl(buildPdpUrl(wp));
  }

  openInstallationModal() {
    this.showInstallationModal = true;
    document.body.style.overflow = 'hidden';
  }

  /** Terms accepted — apply the opt-in. */
  closeInstallationModal() {
    this.installationAgreed = true;
    this.showInstallationModal = false;
    document.body.style.overflow = '';
    // Through chooseService, NOT `wantInstallation = true`: setting the one flag
    // would leave an expert visit selected alongside it, and the server refuses
    // both on a line — the customer would only find out at add-to-cart. This also
    // re-quotes, so the calculator picks up the installation charge.
    this.chooseService('installation');
  }

  /** Terms declined (Cancel or clicking away) — leave the opt-in off. */
  declineInstallationModal() {
    this.installationAgreed = false;
    this.showInstallationModal = false;
    document.body.style.overflow = '';
    // Declining clears the selection outright rather than falling back to a
    // visit the customer never asked for.
    this.chooseService('none');
  }

  navigateToListing() {
    this.router.navigate(['/listing']);
  }
}

